/**
 * [CAMPAIGN-HISTORY 2026-09-04, user] One campaign's whole life, opened by clicking a website in
 * My Inventory. Three columns, matching the layout the user asked for: who the campaign is on the
 * left, the month-by-month story in the middle, summary cards on the right.
 *
 * Written for someone who has never used the app before — the user's requirement was "simple
 * english and easy to understand", so every heading is a plain phrase ("Sold 3 times", "Not with
 * us") and each number is explained in words where it could be misread.
 *
 * Every figure comes from server/services/campaignHistory.ts. Nothing is computed here beyond
 * formatting, so this page cannot drift from the rest of the app.
 *
 * ⚠ Colour convention: `theme.muted` is FULL INK in this app (black on light, white on dark) —
 * see the note in styles/theme.ts. `theme.dim` is reserved for input placeholders and must NOT be
 * used for secondary text. Hierarchy here comes from size and weight, never from greying text out.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { lgPress, lgButtonPrimary, lgButtonSecondary, lgTile, lgOnWhite, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import {
  fetchCampaignHistory, CampaignHistoryData, CampaignDeal, CampaignActivity,
  fetchCampaignPulse, CampaignPulse, // [CLIENT-PULSE 2026-09-09]
  fetchCampaignPulseEmbed, CampaignPulseEmbed, // [CP-ROUTES 2026-09-10]
  campaignPulseFrameUrl, invalidateGetCacheByPrefix, // [CP-FRAME 2026-09-10]
  fetchCampaignOrder, CampaignOrder, CampaignOrderField, CampaignOrderRecord, // [ORDER-CONFIRMATION 2026-09-21]
  fetchCampaignOrderPdf, // [OC-PAGES 2026-09-21]
} from '../services/api';
import { SkeletonBox } from '../components/Skeleton';
import { LoadFailed, isForbidden } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-26] the tab row swipes on phones
import GlassSelect from '../components/GlassSelect'; // [ORDER-CONFIRMATION 2026-09-21] the month filter
import { preloadPdfJs, drawPdfPages } from '../services/ocPdfPages'; // [OC-PAGES 2026-09-21] the OC's pages
import { lightTheme, darkTheme, ThemeType } from '../styles/theme'; // [CP-INSTANT 2026-09-10]
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

/* ── formatting ─────────────────────────────────────────────────────────── */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (n: number | null | undefined) =>
  (n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`);
const money2 = (n: number | null | undefined) =>
  (n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n.toFixed(1)}%`);
const ym = (s: string | null | undefined) => {
  if (!s) return '—';
  const [y, m] = s.split('-');
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${y}`;
};
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];
/** 'YYYY-MM' → "September 2026" — the timeline's month headers. Split, never new Date(): a
 *  date-only string parses as UTC midnight and lands on the previous day in US time zones. */
const monthLong = (s: string) => {
  const [y, m] = s.split('-');
  return `${MONTHS_LONG[Number(m) - 1] || m} ${y}`;
};
const day = (s: string | null | undefined) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${Number(d)} ${MONTHS_SHORT[Number(m) - 1] || m} ${y}`;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 21 → "21st", 22 → "22nd", 31 → "31st". */
const ordinal = (n: number): string => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
};
/** "Sep 2026", "Sep 2026 and Oct 2026", "Sep 2026, Oct 2026 and Nov 2026". */
const listMonths = (ms: string[]): string => {
  const names = ms.map(ym);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/* ── activity tone colours ───────────────────────────────────────────────────
   ⚠ `theme.danger` is #dc2626 in BOTH themes (styles/theme.ts:48 and :83 — byte-identical), which
   on the dark #262a28 surface lands near 3.4:1 and fails small-text contrast. That is exactly the
   fault `greenInk` was introduced to fix for green, so red and amber get the same treatment here:
   a deep shade on light, a light shade on dark. Green already has a token, so it just uses it.
   [SAGE-JADE 2026-09-28] `theme.danger` is now theme-aware too (#c81e1e / #f47a7a); this page keeps
   its own tuned pair below, which was computed against its tints. */
const toneInk = (p: any, tone: string): string => {
  const dark = p.theme.colorScheme === 'dark';
  if (tone === 'good') return p.theme.greenInk;
  if (tone === 'bad') return dark ? '#fda4a1' : '#b42318';
  if (tone === 'warn') return dark ? '#fbbf24' : '#a16207';
  return p.theme.muted;
};
const toneTint = (p: any, tone: string): string => {
  const dark = p.theme.colorScheme === 'dark';
  if (tone === 'good') return p.theme.greenTint;
  if (tone === 'bad') return dark ? 'rgba(253,164,161,0.13)' : 'rgba(180,35,24,0.09)';
  if (tone === 'warn') return dark ? 'rgba(251,191,36,0.14)' : 'rgba(161,98,7,0.11)';
  return p.theme.pill;
};

/* ── shell ──────────────────────────────────────────────────────────────── */
/* THE PAGE ITSELF DOES NOT SCROLL. `/campaign-history` is in AppLayout's $flush list, so PageCard
   is overflow:hidden and height:100%; the height chain (PageCard → PageFade, both 100%) lets this
   fill the card exactly. Each of the three columns is its own scroller instead — see Col.

   ⚠ THE BOTTOM PADDING IS LORE, NOT TASTE. The floating dock (components/BottomNav) sits at
   `bottom: 18px` and stands ~64px tall, so it covers the bottom ~82px of the VIEWPORT. MainArea
   pads 16px, so PageCard's bottom edge is already 16px up — leaving 66px of card still under the
   pills. 84px here puts the end of every column's scroll ~18px clear of the dock, which is what
   makes "the last data for all 3 sections comes above the tab pill" true for each column
   independently, not just for the page as a whole. */
const Page = styled.div`
  height: 100%;
  overflow: hidden;
  padding: 18px 20px 84px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Stacked layout: the columns are full-width rows, so per-column scrollers would trap content
     in three tiny boxes. Hand scrolling back to the page there. */
  ${layoutMax(1240)} {
    height: auto;
    max-height: 100%;
    overflow-y: auto;
  }
`;
/* [BACK-ARROW 2026-09-10, user] The way back is an ARROW ONLY ("only arrow thing"), on the left of
   the Campaign History box. It replaced the "← My Inventory" / "← Inventory" text button that sat
   in the Lifetime card's heading. Where it goes is unchanged — see goBack. There is no text, so
   every use carries an aria-label.

   ⚠⚠ SUBTLE RED, AND WHY IT DOES NOT USE `theme.danger`. The user asked for a red that highlights
   the control in BOTH themes. `theme.danger` is #dc2626 in light AND dark (styles/theme.ts:48 and
   :83 — byte-identical), and on the dark #262a28 surface that lands ~3.4:1, failing small-text
   contrast. So this reuses the page's own theme-aware pair: ink #b42318 on light / #fda4a1 on dark,
   over a matching 9-13% tint. COMPUTED against the blended background, not eyeballed:
   light 5.67:1, dark 5.90:1 — both clear of the 4.5:1 AA floor for normal text. It stays a TINT
   rather than a solid fill so it reads as an accent, not an alarm. [SAGE-JADE 2026-09-28] The
   byte-identical #dc2626 described above was retired; `theme.danger` is now per theme. */
const BackBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  width: 34px; height: 34px;
  background: ${(p) => toneTint(p, 'bad')};
  border: 1px solid ${(p) => toneInk(p, 'bad')}40;
  border-radius: 9px; padding: 0;
  font-size: 14px;
  color: ${(p) => toneInk(p, 'bad')}; cursor: pointer;
  transition: background 160ms cubic-bezier(0.4, 0, 0.2, 1), border-color 160ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover { border-color: ${(p) => toneInk(p, 'bad')}; }
  &:focus-visible { outline: 2px solid ${(p) => toneInk(p, 'bad')}; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
`;
/* One row, three columns, all exactly as tall as the space left under the header. `min-height: 0`
   is the load-bearing line: without it a grid item refuses to shrink below its content, the row
   grows to fit the tallest column, and every per-column scroller silently stops working. */
const Shell = styled.div`
  display: grid; gap: 16px; align-items: stretch;
  grid-template-columns: 300px minmax(0, 1fr) 330px;
  flex: 1 1 auto;
  min-height: 0;

  ${layoutMax(1240)} {
    grid-template-columns: 1fr;
    flex: 0 0 auto;
    min-height: auto;
  }
`;
/* [2026-09-04, user] Each section carries its OWN vertical scroll. Because all three are grid
   items in one stretched row they are equal height by construction — no measuring, no
   ResizeObserver (an earlier attempt measured the right column and handed its height to the other
   two; this replaces it entirely and is far more robust).
   The scrollbar is deliberately NOT styled here: GlobalStyles paints every scrollbar in the app
   through a `*` selector (10px, rounded thumb, theme-aware, hover transition), so inheriting keeps
   this identical to every other scroller in both themes. */
const Col = styled.div`
  display: flex; flex-direction: column; gap: 16px; min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Cards keep their natural height and the COLUMN scrolls; without this they would compress to
     fit and nothing would ever overflow. */
  > * { flex-shrink: 0; }
  /* When a column has spare room the last card takes it, so short columns still reach the bottom
     edge and the three read as one aligned block. */
  > :last-child { flex-grow: 1; }

  ${layoutMax(1240)} {
    overflow-y: visible;
    > :last-child { flex-grow: 0; }
  }
`;
const Card = styled.section`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px; box-shadow: ${(p) => p.theme.shadow};
  overflow: hidden;
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

/* ── middle-column tabs ─────────────────────────────────────────────────────
   [2026-09-04, user] Three sections at the top of the middle column. The strip sits OUTSIDE the
   scroll pane so it stays put while the content under it scrolls.

   ⚠⚠ THEME TRAP, and why the active tab is marked with the ACCENT rather than with brightness.
   The obvious segmented control gives the active tab `surface` on a `pill` track. That inverts
   between themes: light is #ffffff on #e9ebea (active is LIGHTER than the track, reads raised),
   dark is #262a28 on #3d4140 (active is DARKER than the track, reads recessed). Same class of bug
   as the green panels that were byte-identical in both themes and glared on dark.
   So the signal that carries the state is `greenInk`, which is already tuned per theme
   (#1a7f64 light / #1fd8a4 dark) — it reads as "selected" on either ground, and the surface fill
   is then only a supporting cue rather than the whole message. */
/* [BUTTON-STYLE 2026-09-30] Track and tab sizes come from styles/buttons (pillTrack / pillSegment), the
   one switch-pill size; the active marking (surface + greenInk + shadow) is unchanged. */
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  ${pillTrack}
  background: ${(p) => p.theme.pill};
  border: 1px solid ${(p) => p.theme.line};
  flex: 0 0 auto;

  /* [PHONE-LAYOUT 2026-10-26, user] Phones: three equal thirds of a 320 px card cut every label ("Campai…",
     "Client P…", "Order …"). One sideways swipe row instead — each tab as wide as its full label, no scrollbar,
     and the chosen tab brought into view (the page's tabsRef effect): the Lead Generator Performance pattern. */
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border-color: transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;
const Tab = styled.button<{ $active: boolean }>`
  border: 0;
  font: inherit;
  ${pillSegment}
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 160ms cubic-bezier(0.4, 0, 0.2, 1), color 160ms cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(p) => (p.$active ? p.theme.surface : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.greenInk : p.theme.muted)};
  box-shadow: ${(p) => (p.$active ? p.theme.shadow : 'none')};

  &:hover { background: ${(p) => (p.$active ? p.theme.surface : p.theme.buttonHover)}; }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.greenInk}; outline-offset: -2px; }
  @media (prefers-reduced-motion: reduce) { transition: none; }

  /* [PHONE-LAYOUT 2026-10-26] Phones: the full label, never "…" (the row swipes, see Tabs). */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${(p) => (p.$active ? `background-image: linear-gradient(180deg, rgba(255,255,255,${p.theme.colorScheme === 'dark' ? '0.10' : '0.85'}), rgba(255,255,255,0) 60%); box-shadow: inset 0 1px 0 rgba(255,255,255,${p.theme.colorScheme === 'dark' ? '0.14' : '1'}), 0 1px 2px rgba(0,0,0,0.12), 0 6px 14px -8px rgba(0,0,0,0.35);` : '')}
`;

/* A column with something PINNED above its scroller — the middle column's tab strip, and the right
   column's "Go to My Inventory" button. Used by both, hence the generic name. */
const PinnedCol = styled.div`
  display: flex; flex-direction: column; gap: 12px;
  min-width: 0; min-height: 0;
  overflow: hidden;
  ${layoutMax(1240)} { overflow: visible; }
`;
const PinnedScroll = styled.div`
  flex: 1 1 auto; min-height: 0;
  /* [CP-INSTANT 2026-09-10] The anchor for the hidden Client Pulse pane (PANE_HIDDEN). */
  position: relative;
  overflow-y: auto; overflow-x: hidden;
  display: flex; flex-direction: column; gap: 16px;
  > * { flex-shrink: 0; }
  > :last-child { flex-grow: 1; }
  ${layoutMax(1240)} {
    overflow-y: visible;
    > :last-child { flex-grow: 0; }
  }
`;
/* Honest empty state for a section that has not been specified yet — it says so rather than
   showing an empty card that reads as broken or as "this campaign has no data". */
const NotBuilt = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  p { margin: 0; }
  p + p { margin-top: 8px; font-size: 13px; }
`;
const CardTitle = styled.h2`
  margin: 0; padding: 13px 16px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  border-bottom: 1px solid ${(p) => p.theme.line};
`;
/* A CardTitle that carries an action on its right. Same padding and rule as CardTitle so the two
   are interchangeable and the card headings stay aligned down the column. */
const CardTitleRow = styled(CardTitle)`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`;
const CardBody = styled.div`padding: 14px 16px;`;

/* ── left rail ──────────────────────────────────────────────────────────── */
const IdHead = styled.div`padding: 16px; display: flex; flex-direction: column; gap: 4px;`;
/* [BACK-ARROW 2026-09-10, user] Back arrow on the left; the page name and the website stacked to
   its right. min-width: 0 lets a long website wrap instead of pushing the card wider. */
const IdTop = styled.div`display: flex; align-items: center; gap: 12px;`;
const IdText = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 0;`;
const SiteName = styled.span`
  font-size: 19px; font-weight: 800; line-height: 1.25;
  color: ${(p) => p.theme.text}; word-break: break-all;
`;
const SubName = styled.span`font-size: 13px; color: ${(p) => p.theme.muted}; overflow-wrap: anywhere;`;
const ChipRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;`;
const Badge = styled.span<{ $tone: 'good' | 'bad' | 'wait' | 'flat' }>`
  display: inline-flex; align-items: center; border-radius: 999px;
  padding: 3px 10px; font-size: 11.5px; font-weight: 700;
  background: ${(p) => (p.$tone === 'good' ? p.theme.greenTint
    : p.$tone === 'bad' ? 'rgba(220,38,38,0.12)'
    : p.$tone === 'wait' ? 'rgba(201,138,27,0.14)' : p.theme.pill)};
  color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk
    : p.$tone === 'bad' ? p.theme.danger
    : p.$tone === 'wait' ? '#c98a1b' : p.theme.text)};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;
const Facts = styled.dl`
  margin: 0; display: grid; gap: 11px;
  dt {
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: ${(p) => p.theme.muted};
  }
  dd { margin: 2px 0 0; font-size: 13.5px; font-weight: 500; color: ${(p) => p.theme.text}; word-break: break-word; }
  /* [ABOUT 2026-09-07, user] "for the sale owner and pm name, make that text appear to be big then
     the other data as this is the main highlighting thing". Sales Owner and Current PM are the two
     people who own this campaign, so they read a size up from every other fact. Size and weight
     only — no colour of their own, so the pair stays legible in both themes. */
  .hero dd { font-size: 18px; font-weight: 650; line-height: 1.25; letter-spacing: -0.01em; }
  /* The "(original was X)" note is context for the big name, not a headline itself: it drops to a
     second line at normal reading size. theme.muted is full ink here, not a placeholder grey. */
  .hero dd .sub { display: block; margin-top: 3px; font-size: 12.5px; font-weight: 500; color: ${(p) => p.theme.muted}; letter-spacing: 0; }
  /* [PHONE-LAYOUT 2026-10-26] Phones: labels at 11 px, the phone minimum (were 10.5). */
  @media (max-width: 768px) { dt { font-size: 11px; } }
`;

/* ── Campaign timeline ───────────────────────────────────────────────────────
   [TIMELINE 2026-09-10, user] Replaces the old feed after the manager called it "lame, not
   professional". Every line of data is the same; the layout is new:
   · ONE grid for every event — date · marker · words · amount — so each line reads the same way
     and the amounts line up in one column at the card's right edge. ⛔ No max-width: an 800px cap
     left the right half of the card blank on a wide screen and read as broken (user, 09-10).
   · A continuous rail through the markers. It had been removed only because the centred
     milestone bands broke it; milestones now sit ON the rail instead of across it.
   · Milestones stay unmissable — "it will not disappear from the sight": a tinted row, a larger
     marker in the event's tone, a category kicker and a bold title in that tone.
   · The month header carries what the boxed PM table used to (PM · retained · % · status) and
     stays pinned while its month scrolls under it. Colour only where something is off.
   · Year dividers and one continuous flow — nothing folded (a collapsible list was rejected).
   Colours come from toneInk / toneTint above and theme tokens only; no new token. */
type EvTone = CampaignActivity['tone'];

/* Milestone fill and edge. toneTint's flat case is theme.pill, a SOLID #3d4140 on dark — heavier
   than the red and green tints, so a PM change would out-shout a cancellation. Flat gets its own
   faint wash in both themes instead. */
const majorTint = (p: any, tone: EvTone): string => {
  if (tone !== 'flat') return toneTint(p, tone);
  return p.theme.colorScheme === 'dark' ? 'rgba(162,162,162,0.10)' : 'rgba(22,25,23,0.035)';
};
const majorEdge = (p: any, tone: EvTone): string => {
  if (tone !== 'flat') return `${toneInk(p, tone)}33`;
  return p.theme.colorScheme === 'dark' ? 'rgba(162,162,162,0.24)' : 'rgba(22,25,23,0.10)';
};

/* overflow: clip rather than the Card's hidden. hidden makes the card its own scroll container,
   and a sticky month header inside it would then never stick (EOMPage uses the same fix). */
const TimelineCard = styled(Card)`
  overflow: clip;
  /* [LIQUID-GLASS 2026-11-02, user] "make this part same what we have before": the timeline keeps the old card (no glass) */
  && { border: 1px solid ${(p) => p.theme.line}; background: ${(p) => p.theme.surface}; box-shadow: ${(p) => p.theme.shadow}; }
`;
const TitleRange = styled.span`
  font-size: 12px; font-weight: 600; letter-spacing: 0; text-transform: none;
  color: ${(p) => p.theme.muted};
  font-variant-numeric: tabular-nums; white-space: nowrap;
`;
const Timeline = styled.div`
  padding: 0 16px 16px;
`;
const YearDivider = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin: 22px 0 4px;
  font-size: 11.5px; font-weight: 800; letter-spacing: 0.16em;
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums;
  &::after { content: ''; flex: 1; height: 1px; background: ${(p) => p.theme.line}; }
  &:first-child { margin-top: 14px; }
`;
const MonthSection = styled.section``;
/* Pinned while its own month scrolls under it. Sticky is scoped to the MonthSection, so the next
   month's header pushes this one away instead of stacking on it; the solid surface lets rows pass
   BEHIND it. 16px keeps the month above every event title — the old month label was smaller than
   the milestone titles it was meant to organise. Its 10px inset lines up with the date column. */
const MonthHead = styled.h3`
  position: sticky; top: 0; z-index: 3;
  margin: 0; padding: 12px 10px 9px;
  display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between;
  gap: 4px 14px;
  background: ${(p) => p.theme.surface};
  border-bottom: 1px solid ${(p) => p.theme.line};
  font-size: 16px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.3;
  color: ${(p) => p.theme.text};
`;
const MonthMeta = styled.span`
  display: inline-flex; flex-wrap: wrap; align-items: center; gap: 3px 8px;
  font-size: 12.5px; font-weight: 600; letter-spacing: 0; line-height: 1.5;
  color: ${(p) => p.theme.muted};
  font-variant-numeric: tabular-nums;

  /* [PHONE-LAYOUT 2026-10-26, user] Phones: these facts wrap onto 2-3 lines, and a separate "·" (MetaSep) was left at a
     line's end ("Shifa Khan ·") or began the next line ("· ✓ Retained"). On phones each fact carries its own separator,
     a 21 px slot in front of it with the "·" centred (the laptop's 8 + 5 + 8 px), and the box moves 21 px left with
     that strip clipped: the slot of the first fact on every line falls in the clipped strip, so a line never starts or
     ends with "·". The In progress / Upcoming chip keeps its slot as a margin (StateChip). Desktop unchanged. */
  @media (max-width: 768px) {
    column-gap: 0;
    margin-left: -21px;
    clip-path: inset(0 0 0 21px);
    & > [aria-hidden] { display: none; }
    & > *::before {
      content: '·';
      display: inline-block;
      width: 21px;
      text-align: center;
      font-weight: 600;
      color: ${(p) => p.theme.borderHover};
    }
  }
`;
const MetaSep = styled.span`color: ${(p) => p.theme.borderHover};`;
const MetaTone = styled.span<{ $tone: EvTone }>`
  font-weight: 700;
  color: ${(p) => (p.$tone === 'flat' ? p.theme.muted : toneInk(p, p.$tone))};
`;
/* A normal month (Retained, 100%) stays in plain ink with a small green tick — colour is kept for
   the months where something is off, so an At-Risk or Cancelled month is the one that stands out. */
const MetaOk = styled.span`
  font-weight: 700;
  i { font-size: 10px; margin-right: 4px; color: ${(p) => p.theme.greenInk}; }
`;
/* [TIMELINE 2026-09-10, user] "in progress should be in green colour" — the running month's chip
   uses the page's green pair (theme-tuned, so it holds in dark mode). Upcoming stays neutral. */
const StateChip = styled.span<{ $live?: boolean }>`
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 999px;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  background: ${(p) => (p.$live ? p.theme.greenTint : p.theme.pill)};
  color: ${(p) => (p.$live ? p.theme.greenInk : p.theme.text)};
  @media (max-width: 768px) {
    font-size: 11px; /* [PHONE-LAYOUT 2026-10-26] */
    /* [PHONE-LAYOUT 2026-10-26, user] In a month's facts (MonthMeta): the chip opens the first line, so its separator
       slot is a 21 px margin (in the clipped strip) instead of a "·" inside the pill; the fact after it gets a blank
       slot and the chip gives back 13 px, so the two sit 8 px apart, as on the laptop. */
    ${MonthMeta} > & { margin: 0 -13px 0 21px; }
    ${MonthMeta} > &::before { content: none; }
    ${MonthMeta} > & + *::before { content: ''; }
  }
`;
const EventList = styled.ol`
  list-style: none; margin: 0; padding: 6px 0 2px;
`;
/* Every event, routine or milestone, on the same grid. A milestone adds only a fill and an inset
   edge (box-shadow, so it cannot shift the grid off the rail) — never a coloured side bar, which
   the user had removed.
   THE RAIL is one segment per event, from its marker down to the next one (the last has none), so
   a one-event month shows a dot and no dangling stub. x = row padding 10 + date column 92 + gap 12
   + half the marker column 8 = 122; y = row padding 9 + marker centre 10 = 19; bottom -25 reaches
   the next marker across the up-to-6px gap a milestone's margin adds. Keep in step with the grid. */
const Event = styled.li<{ $major: boolean; $tone: EvTone }>`
  position: relative;
  display: grid;
  grid-template-columns: 92px 16px minmax(0, 1fr) auto;
  column-gap: 12px;
  align-items: start;
  padding: 9px 10px;
  border-radius: 10px;
  margin: ${(p) => (p.$major ? '6px 0' : '0')};
  background: ${(p) => (p.$major ? majorTint(p, p.$tone) : 'transparent')};
  box-shadow: ${(p) => (p.$major ? `inset 0 0 0 1px ${majorEdge(p, p.$tone)}` : 'none')};
  &:not(:last-child)::after {
    content: ''; position: absolute;
    left: 121.5px; top: 19px; bottom: -25px; width: 1px;
    background: ${(p) => p.theme.line};
  }
  /* [PHONE-LAYOUT 2026-10-26, user] Phones (560 px or less): the four columns left an event's words about 80 px, one
     or two words a line ("Retention · / via The / Scale / Agency"). Now the date and the amount share the first line
     and the words take the full width under them; the marker stays on the rail in its own 16 px column (the rail at
     row padding 10 + 8 = 18 px). Was: 72px 16px minmax(0, 1fr) auto, the rail at 99.5 px. */
  @media (max-width: 560px) {
    grid-template-columns: 16px minmax(0, 1fr) auto;
    grid-template-areas: 'mark when amt' 'mark body body';
    column-gap: 10px;
    row-gap: 2px;
    &:not(:last-child)::after { left: 17.5px; }
  }
`;
/* A real day reads bold ("23 Jun 2026"); a month-level fact shows only its month, in regular
   weight, so the two can never be confused — and no day is ever invented. */
const When = styled.time<{ $day: boolean }>`
  font-size: 12px; line-height: 20px;
  font-weight: ${(p) => (p.$day ? 700 : 500)};
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums; white-space: nowrap;
  @media (max-width: 560px) { font-size: 11px; grid-area: when; /* [PHONE-LAYOUT 2026-10-26] see Event */ }
`;
const Marker = styled.span<{ $major: boolean; $tone: EvTone }>`
  position: relative; z-index: 1;
  justify-self: center;
  width: ${(p) => (p.$major ? '12px' : '8px')};
  height: ${(p) => (p.$major ? '12px' : '8px')};
  margin-top: ${(p) => (p.$major ? '4px' : '6px')};
  border-radius: 999px;
  background: ${(p) => (p.$tone === 'flat' ? p.theme.muted : toneInk(p, p.$tone))};
  box-shadow: 0 0 0 3px ${(p) => p.theme.surface};
  @media (max-width: 560px) { grid-area: mark; /* [PHONE-LAYOUT 2026-10-26] see Event */ }
`;
const EvBody = styled.div`
  min-width: 0;
  @media (max-width: 560px) { grid-area: body; /* [PHONE-LAYOUT 2026-10-26] the full width, see Event */ }
`;
const EvTitle = styled.div<{ $major: boolean; $tone: EvTone }>`
  font-size: ${(p) => (p.$major ? '14px' : '13.5px')};
  font-weight: ${(p) => (p.$major ? 800 : 600)};
  line-height: 20px;
  color: ${(p) => (p.$major && p.$tone !== 'flat' ? toneInk(p, p.$tone) : p.theme.text)};
  overflow-wrap: anywhere;
`;
const Kicker = styled.span<{ $tone: EvTone }>`
  display: inline-block; margin-right: 8px; vertical-align: 1px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase;
  line-height: 1;
  color: ${(p) => (p.$tone === 'flat' ? p.theme.muted : toneInk(p, p.$tone))};
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] was 10 */
`;
const EvDetail = styled.div`
  margin-top: 1px;
  font-size: 12.5px; font-weight: 500; line-height: 1.45;
  color: ${(p) => p.theme.muted};
  overflow-wrap: anywhere;
`;
const EvAmount = styled.div<{ $major: boolean; $tone: EvTone }>`
  font-size: 13.5px; font-weight: 700; line-height: 20px;
  font-variant-numeric: tabular-nums; white-space: nowrap; text-align: right;
  color: ${(p) => (p.$major && p.$tone !== 'flat' ? toneInk(p, p.$tone) : p.theme.text)};
  @media (max-width: 560px) { grid-area: amt; /* [PHONE-LAYOUT 2026-10-26] beside the date, see Event */ }
`;

/** Category word for a milestone, shown before its title only when the title does not already
 *  say it: "Sold by X" gets NEW SALE, "Refund" gets nothing. */
const KICKER: Partial<Record<CampaignActivity['kind'], string>> = {
  started: 'First payment', joined: 'Joined', returned: 'Came back',
  pm_assigned: 'PM assigned', pm_changed: 'PM change', sold: 'New sale', upsell: 'Upsell',
  discount: 'Discount', price_change: 'Price change', type_change: 'Type change',
  moved: 'Admin change', refund: 'Refund', dispute_open: 'Dispute', dispute_lost: 'Dispute',
  dispute_won: 'Dispute', cancelled: 'Cancelled', left: 'Left',
};
const kickerFor = (a: CampaignActivity): string | null => {
  const k = a.major ? KICKER[a.kind] : undefined;
  return k && !a.title.toLowerCase().includes(k.toLowerCase()) ? k : null;
};
/** Money that went back out is signed, so a refund can never be read as money in. */
const signedAmount = (a: CampaignActivity): string =>
  (a.kind === 'refund' || a.kind === 'dispute_lost' ? `−${money2(a.amount)}` : money2(a.amount));
/** Inventory status → tone for the month header. Only an exact "Retained" is the quiet good case. */
const statusTone = (s: string | null | undefined): EvTone => {
  const v = (s || '').trim().toLowerCase();
  if (v.includes('cancel')) return 'bad';
  if (v.includes('risk') || v.includes('partial') || v.includes('pause')) return 'warn';
  if (v === 'retained') return 'good';
  return 'flat';
};

type TimelineMonth = { ym: string; rows: CampaignActivity[]; summary: boolean };
type TimelineYear = { year: string; months: TimelineMonth[] };
/** Activities arrive newest first; fold them into year → month for the dividers and the pinned
 *  headers. `pm_month` is no longer a row of its own — it becomes that month's header line. */
function groupTimeline(activities: CampaignActivity[]): TimelineYear[] {
  const years: TimelineYear[] = [];
  for (const a of activities) {
    const year = a.month.slice(0, 4);
    let y = years[years.length - 1];
    if (!y || y.year !== year) { y = { year, months: [] }; years.push(y); }
    let m = y.months[y.months.length - 1];
    if (!m || m.ym !== a.month) { m = { ym: a.month, rows: [], summary: false }; y.months.push(m); }
    if (a.kind === 'pm_month') m.summary = true; else m.rows.push(a);
  }
  return years;
}

/* ── right column ───────────────────────────────────────────────────────── */
const Rows = styled.div`display: flex; flex-direction: column;`;
const Row = styled.div`
  display: grid; gap: 3px; padding: 12px 16px;
  border-top: 1px solid ${(p) => p.theme.line};
  &:first-child { border-top: none; }
`;
const RowTop = styled.div`display: flex; align-items: baseline; justify-content: space-between; gap: 10px;`;
const RowName = styled.span`font-size: 13.5px; font-weight: 700;`;
const RowAmt = styled.span`font-size: 13.5px; font-weight: 700; white-space: nowrap; font-variant-numeric: tabular-nums;`;
const RowSub = styled.span`font-size: 12px; color: ${(p) => p.theme.muted};`;
const Totals = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: ${(p) => p.theme.line};
  > div { background: ${(p) => p.theme.surface}; padding: 14px 16px; display: grid; gap: 3px; }
  /* The headline figure spans both columns and sits above the 2x2 breakdown. */
  > .wide { grid-column: 1 / -1; }
  > .wide .v { font-size: 24px; }
  .k { font-size: 10.5px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: ${(p) => p.theme.muted}; }
  @media (max-width: 768px) { .k { font-size: 11px; } } /* [PHONE-LAYOUT 2026-10-26] */
  .v { font-size: 19px; font-weight: 800; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; }
  .v.good { color: ${(p) => p.theme.greenInk}; }
`;
const Foot = styled.p`
  margin: 0; padding: 12px 16px; font-size: 12.5px; color: ${(p) => p.theme.muted};
  border-top: 1px solid ${(p) => p.theme.line}; background: ${(p) => p.theme.bg};
`;
/* Centres the button under the error text. Msg is text-align:center, but BackBtn is an inline-flex
   with a fixed size, so it needs a flex row of its own to actually sit in the middle. */
const MsgAction = styled.div`display: flex; justify-content: center; margin-top: 16px;`;
const Msg = styled.div`
  background: ${(p) => p.theme.surface}; border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px; padding: 28px; text-align: center; color: ${(p) => p.theme.text};
  box-shadow: ${(p) => p.theme.shadow};
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

/* ── Client Pulse Details ────────────────────────────────────────────────────
   [CLIENT-PULSE 2026-09-09, user] ClientPulse's read on this website, in our own chrome.

   ⭐ NO NEW THEME TOKEN. ClientPulse's zones map exactly onto the tones this page already owns —
   green/LOW -> good, amber/ELEVATED -> warn, red/HIGH -> bad — so every colour here comes from
   toneInk/toneTint at the top of this file. That matters: styles/theme.ts has NO amber token
   (see the note above toneInk), and inventing one for this tab would have been the third place
   in the app to define "amber" and the first to get it wrong on one of the two themes. */
const zoneTone = (zone: string | null | undefined): 'good' | 'warn' | 'bad' | 'flat' => {
  const z = (zone || '').toLowerCase();
  if (z === 'green' || z === 'low') return 'good';
  if (z === 'amber' || z === 'elevated') return 'warn';
  if (z === 'red' || z === 'high') return 'bad';
  return 'flat';
};
/** "amber" -> "Amber", "LOW" -> "Low". Their zones arrive in two different cases. */
const zoneLabel = (zone: string | null | undefined): string => {
  if (!zone) return 'Not scored';
  const z = zone.toLowerCase();
  return z.charAt(0).toUpperCase() + z.slice(1);
};
/** A ClientPulse score is 0-100. null means they could not score it — never render that as 0.
    [CP-POLISH 2026-09-10, user] Scores read "58 / 100" in whole numbers. This replaces the "58.2%"
    of 2026-09-09 at the user's say-so: a score out of 100 is not a percentage, and "0.0% chance of
    losing them" read as a guarantee. ⛔ The N/A branch stays first — an unscored KPI is "N/A",
    never "N/A / 100". Their risk score can pass 100 on a floored account; it is printed as it is,
    and only the bar under it is clamped. */
const Of100 = styled.span`
  margin-left: 0.25em; font-size: 0.5em; font-weight: 700; letter-spacing: 0;
`;
const Score100: React.FC<{ v: number | null | undefined }> = ({ v }) =>
  (v == null ? <>N/A</> : <>{Math.round(v)}<Of100>/ 100</Of100></>);

/* [2026-09-09, user] "for all these 6 things, add the tool tip also which has the simple English
   explanation for the data that we have in the six KPI."

   ⭐ ONE MAP, PLAIN `title=` ATTRIBUTES — the app's own convention (mirrors PaymentsPage's
   IN_BANK_2_TOOLTIPS). components/GlobalTooltip is mounted in App.tsx and works off the rendered
   DOM: it strips a real `title` on hover, draws the themed glass capsule after a ~600ms dwell, and
   puts the attribute back on leave. So nothing tooltip-specific is wired up here, the capsule is
   themed in both modes for free, and "\n" survives (the capsule renders white-space: pre-line).

   ⚠ THE COPY IS MEASURED, NOT GUESSED. Each line describes how ClientPulse actually computes that
   figure, read off their own reasoning_text and score breakdowns on 2026-09-09:
     • Contact — their reasoning for this very campaign reads "email effort 60 (>= 6 bar) = 50;
       meeting effort: no meeting, 0 invites + 0 calls (need 4 AND 4) = 0", and a campaign WITH a
       meeting scores "email 50 + meeting 50 = 100". Hence the 50% ceiling, which is the single
       most useful thing to say here — it explains why this campaign reads exactly 50%.
     • Our replies — their response breakdown carries "response_bar_hours: 24".
     • Their mood — outbound emails carry NULL sentiment; only inbound is scored.
     • Meetings — meeting_quality_score is populated for just 24% of active accounts, so N/A is
       the ordinary case and the tooltip says so rather than leaving it looking broken.

   [2026-09-09, user] "make the tooltip for these 6 short, at max 3 lines only." So each is EXACTLY
   three lines, and every line is kept under ~48 characters — GlobalTooltip's capsule is
   max-width 340px at 12.5px, so a longer line WRAPS and silently becomes two, which is how a
   "3 line" tooltip turns into six on screen. Count characters, not just newlines, when editing. */
/* [CP-TIPS 2026-09-10, user] "you have added that combined the three figures below, but what do I
   guess with that data? ... if someone is new and he's just seeing contact and then hundred, how
   will they understand". Rewritten so each tooltip says WHAT the number measures, WHAT earns the
   points and HOW to read it — taken from ClientPulse's own score workings (their clientpulse_dev
   snapshots, 281 active clients, read-only, 2026-09-10; research only, the tab still reads the API):
     • How the campaign is doing = contact 30% + mood 25% + replies 25% + meetings 20% — fits all
       58 clients that have all four to within 0.07. An N/A figure adds NOTHING (no re-weighting, so
       with no meetings the ceiling is 80), EXCEPT a silent client that has only Contact: then the
       score IS Contact (72 of 72). The old "three figures" was wrong — there are four.
     • Contact = 50 for our emails to them (6+ earns the full 50, fewer earn part) + 50 for a meeting
       held, or else 4 meeting invites AND 4 calls. So 50 = one half done, 100 = both.
     • Their mood = AI-read tone of the client's OWN emails over 30 working days (70%) + how few of
       them are negative (30%). N/A exactly when they sent none (80 of 80).
     • Our replies = mainly whether we reply within 24 hours (70%); unanswered emails lower it.
     • Meetings = an AI read of how their meetings went (tracks meeting sentiment, r = 0.83).
     • Chance of losing them = warning signs added up (silence, frustration, refund or cancel talk,
       one-sided emails); the client replying takes it down. Low up to ~31, Elevated ~32-60, High ~62+.
   [2026-09-10, user] "I want the tooltip at max three lines only, and it should explain all the data
   properly in simple English so that someone can read the three lines and understand what actually
   this KPI is for." So each tooltip answers, in plain words and in this order: what the figure is
   FOR · what moves it · how to read the number. No percentages or formula notation — the weights
   above are kept here for the next person, not put in front of a manager.
   Still 3 lines each, every line at most 46 characters (the capsule is 340px wide and wraps a longer
   line, which silently turns 3 lines into 4). */
const PULSE_TIPS = {
  health:
    'Overall health of our work with this client,\n'
    + 'blended from the 4 scores below, Contact most.\n'
    + 'Green = healthy, amber = watch, red = act.',
  risk:
    'How likely this client is to leave us.\n'
    + 'Rises with silence, complaints or talk of\n'
    + 'refunds or cancelling. High = act now.',
  contact:
    'Are we keeping in touch with this client?\n'
    + 'Half the score: we emailed them 6+ times.\n'
    + 'Other half: a meeting, or 4 invites + 4 calls.',
  mood:
    'How happy the client sounds in their emails,\n'
    + 'read by AI from the last 30 working days.\n'
    + 'Only their emails count. N/A = none sent.',
  replies:
    'How fast and fully we answer their emails.\n'
    + 'Best score: every reply within 24 hours.\n'
    + 'Emails left unanswered pull it down.',
  meetings:
    'How well our meetings with them are going,\n'
    + 'judged by AI from what was said in them.\n'
    + 'N/A until there is a meeting to judge.',
};

/* The two headline scores, side by side. They are the only numbers on this tab that get a tinted
   panel of their own — everything below is supporting detail, and giving it the same treatment
   would have flattened the hierarchy into eight equally loud boxes. */
const PulseHeads = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  padding: 14px 16px;
  /* Two tinted panels side by side need room to breathe; below this they stack rather than
     squeeze, because a 3-digit score plus a zone word will not sit in half of a narrow column. */
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const PulseHead = styled.div<{ $tone: 'good' | 'bad' | 'warn' | 'flat' }>`
  padding: 13px 14px 14px;
  border-radius: 12px;
  background: ${(p) => toneTint(p, p.$tone)};
  border: 1px solid ${(p) => toneInk(p, p.$tone)}33;
  display: grid; gap: 7px; min-width: 0;
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;
const PulseHeadTop = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
`;
const PulseLabel = styled.span`
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] */
`;
const PulseScore = styled.span<{ $tone: 'good' | 'bad' | 'warn' | 'flat' }>`
  font-size: 30px; font-weight: 800; line-height: 1; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$tone === 'flat' ? p.theme.text : toneInk(p, p.$tone))};
`;
const PulseZone = styled.span<{ $tone: 'good' | 'bad' | 'warn' | 'flat' }>`
  font-size: 11.5px; font-weight: 700; white-space: nowrap;
  color: ${(p) => (p.$tone === 'flat' ? p.theme.muted : toneInk(p, p.$tone))};
`;
/* The bar is decoration for a number that is already printed, so it is aria-hidden and the width
   is clamped: ClientPulse's risk score can exceed 100 on a floored account (98.1 seen live, and
   the formula can overshoot), which would otherwise paint outside the track. */
const PulseBar = styled.div`
  height: 6px; border-radius: 999px; overflow: hidden;
  background: ${(p) => p.theme.line};
`;
const PulseBarFill = styled.div<{ $tone: 'good' | 'bad' | 'warn' | 'flat'; $pct: number }>`
  height: 100%; border-radius: 999px;
  width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
  background: ${(p) => (p.$tone === 'flat' ? p.theme.borderHover : toneInk(p, p.$tone))};
`;
const PulseSays = styled.span`
  font-size: 12px; color: ${(p) => p.theme.muted}; line-height: 1.35;
`;

/* The four KPIs. A plain grid rather than four more tinted panels — see the note on PulseHeads. */
const PulseKpis = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 1px; background: ${(p) => p.theme.line};
  border-top: 1px solid ${(p) => p.theme.line};
  > div {
    background: ${(p) => p.theme.surface};
    padding: 13px 16px; display: grid; gap: 5px; min-width: 0;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;
const KpiName = styled.span`
  font-size: 10px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] was 10 */
`;
/* [CLIENT-PULSE 2026-09-09] A null KPI reads "N/A" at a smaller size than a real score, so an
   unscored KPI never looks like a low one. ClientPulse's contract is explicit that null means
   "insufficient data" and must never render as 0 — and meeting health is null on 76% of active
   accounts, so this is the ordinary case, not a rare edge. */
const KpiValue = styled.span<{ $na: boolean }>`
  font-size: ${(p) => (p.$na ? '15px' : '21px')};
  font-weight: 800; letter-spacing: -0.01em; line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$na ? p.theme.muted : p.theme.text)};
`;
const KpiSays = styled.span`font-size: 11.5px; color: ${(p) => p.theme.muted}; line-height: 1.35;`;

/* ClientPulse's own timeline, embedded rather than rebuilt — their words: "any timeline
   improvement we ship appears in your app automatically".
   [CP-POLISH 2026-09-10, user] "use the theme as we have in our system for all the client pulse
   data". The frame now carries OUR colours (timelineFrameSrc below), so it sits edge to edge inside
   its card as part of the page, not as a bordered foreign panel. ClientPulse's own header strip is
   its title — the user chose their heading over ours ("use the data as per the client pulse"), so
   this card has no CardTitle and nothing is said twice.
   It fills whatever height the column has left (the card is the pane's last child, which the pane
   grows), and never drops below 480px.
   [CP-INSTANT 2026-09-10, user] "make this below part also fast … immediately" and "when I click on
   the theme change … no loading to that data". The whole Client Pulse pane is now mounted as soon as
   the campaign has loaded and is only HIDDEN on the other tabs, so the timeline loads in the
   background, is usually ready before the tab is opened, and survives tab switches. Its address
   holds BOTH our themes and never changes on a toggle — the page inside is told which to show. */
/* Hidden WITHOUT display:none. display:none throws the frame's layout away, and with it the reader's
   scroll position inside the timeline — measured: it reset to the top after a tab switch. Here the
   pane keeps its layout but takes no space (out of the flow, zero height, clipped), cannot be seen,
   clicked or reached by keyboard or screen reader. PinnedScroll is position:relative for this. */
const PANE_HIDDEN = 'position: absolute; top: 0; left: 0; right: 0; height: 0; overflow: hidden; visibility: hidden; pointer-events: none;';
const PulsePane = styled.div<{ $hidden: boolean }>`
  display: flex; flex-direction: column; gap: 16px; min-width: 0;
  > * { flex-shrink: 0; }
  > :last-child { flex-grow: 1; }
  ${layoutMax(1240)} { > :last-child { flex-grow: 0; } }
  ${(p) => (p.$hidden ? PANE_HIDDEN : '')}
`;
const PulseTimelineCard = styled(Card)<{ $hidden?: boolean }>`
  display: ${(p) => (p.$hidden ? 'none' : 'flex')}; flex-direction: column;
  /* [LIQUID-GLASS 2026-11-02, user] "make this part same what we have before": the timeline keeps the old card (no glass) */
  && { border: 1px solid ${(p) => p.theme.line}; background: ${(p) => p.theme.surface}; box-shadow: ${(p) => p.theme.shadow}; }
`;
const PulseFrameArea = styled.div`
  position: relative;
  flex: 1 1 auto; display: flex; flex-direction: column; min-height: 480px;
`;
const PulseFrame = styled.iframe`
  display: block; width: 100%;
  flex: 1 1 auto; height: 560px; min-height: 480px;
  border: 0;
  background: ${(p) => p.theme.bg};
`;
/* [CP-FRAME 2026-09-10] Covers the frame until its page has loaded. Our server fetches that page from
   ClientPulse first (~1-2 s), and a blank panel for that long reads as broken. */
const PulseFrameCover = styled.div`
  position: absolute; inset: 0;
  padding: 14px 16px;
  background: ${(p) => p.theme.surface};
`;

/** Bare hex for ClientPulse's embed ("#262a28" -> "1e293b"). Anything else returns null and is left
 *  out, because their validator answers a bad value with a 422 page INSIDE the frame. */
const bareHex = (c: string): string | null => {
  const h = c.trim().replace(/^#/, '');
  return /^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(h) ? h : null;
};
/* [CP-POLISH 2026-09-10, user] Our theme, handed to ClientPulse's timeline as query parameters of
   the embed page (their openapi documents them on /embed/timeline/{account_id}; measured working on
   2026-09-10). Built from the SAME tokens this page paints with, so a change to styles/theme.ts
   reaches the timeline with no second edit:
     bg     -> theme.bg       the ground under the messages, like our page ground between cards
     card   -> theme.surface  their header strip and each message, like our cards
     text, muted -> full ink  (muted IS full ink in this app — see styles/theme.ts)
     line   -> theme.line
     accent -> our warn amber. It colours their HIGH / MEDIUM / LOW urgency tags; green would make
               "HIGH urgency" read as good news.
   Radius 10 and no shadow match the page's own inner panels.
   [CP-FRAME 2026-09-10, user] The frame loads the page from OUR server, which adds the app's
   scrollbar style ("i want it to be my app related") — so this builds the address of our route,
   not theirs, and the link with its token never reaches the browser. The server re-validates every
   value.
   [CP-INSTANT 2026-09-10, user] BOTH themes go in the address — l_… from lightTheme, d_… from
   darkTheme, the same two token sets the app paints with — so the address never changes when the
   reader toggles the theme. The server builds one page holding both; the frame is only told which
   to show (see PulseTimeline). */
function themeColours(prefix: 'l_' | 'd_', t: ThemeType): Record<string, string> {
  const colours: Record<string, string> = {
    bg: t.bg,
    card: t.surface,
    text: t.text,
    muted: t.muted,
    line: t.line,
    accent: toneInk({ theme: t }, 'warn'),
  };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(colours)) {
    const h = bareHex(v);
    if (h) out[prefix + k] = h;
  }
  return out;
}
function timelineFrameSrc(website: string): string {
  return campaignPulseFrameUrl(website, {
    ...themeColours('l_', lightTheme),
    ...themeColours('d_', darkTheme),
    radius: '10',
    shadow: 'none',
  });
}

/** When ClientPulse last scored this campaign, in IST like the app's own clocks:
 *  "2026-09-09 18:45:01Z" -> "10 Sep 2026, 12:15 AM IST". IST has no daylight saving, so the fixed
 *  +5:30 is exact. Falls back to their snapshot date alone if the timestamp is missing or odd. */
const scoredAt = (computedAt: string | null, snapshotDate: string | null): string | null => {
  const d = computedAt ? new Date(computedAt.trim().replace(' ', 'T')) : null;
  if (d && !Number.isNaN(d.getTime())) {
    const ist = new Date(d.getTime() + 330 * 60_000);
    const h = ist.getUTCHours();
    const mm = String(ist.getUTCMinutes()).padStart(2, '0');
    return `${ist.getUTCDate()} ${MONTHS_SHORT[ist.getUTCMonth()]} ${ist.getUTCFullYear()}, ${h % 12 || 12}:${mm} ${h < 12 ? 'AM' : 'PM'} IST`;
  }
  return snapshotDate ? day(snapshotDate) : null;
};

/* [CP-POLISH 2026-09-10, user] A real button for the one thing a reader can do about a temporary
   failure. The old copy said "move to another section and back to try again", which never retried
   anything — the fetch only re-runs when one of its inputs changes. */
// [BUTTON-STYLE 2026-09-30] The green primary from styles/buttons; margin stays (layout).
const RetryBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  margin-top: 16px;
  &:focus-visible { outline: 2px solid ${(p) => p.theme.greenInk}; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
/* Shown only when ClientPulse's PM or monthly amount disagrees with this page — one quiet warn-tone
   line in the source footer, never a banner. PM ownership agreed on 277 of 277 live campaigns on
   2026-09-09, so on almost every page this never renders. */
const PulseNote = styled.span`
  display: block; margin-top: 4px;
  font-weight: 600; color: ${(p) => toneInk(p, 'warn')};
`;

/* [2026-09-04, user] The middle column's three sections. Only 'activities' is specified so far. */
const MID_TABS = [
  { key: 'activities' as const, label: 'Campaign Activities' },
  { key: 'pulse' as const, label: 'Client Pulse Details' },
  { key: 'order' as const, label: 'Order Confirmation' },
];
type MidTab = (typeof MID_TABS)[number]['key'];

/* ── helpers ────────────────────────────────────────────────────────────── */
/** One month row. Collapsed it shows received-of-placed; opened it breaks the month down. */
const DealRows: React.FC<{ deals: CampaignDeal[]; emptyText: string }> = ({ deals, emptyText }) => {
  if (!deals.length) return <CardBody><RowSub>{emptyText}</RowSub></CardBody>;
  return (
    <Rows>
      {deals.map((d, i) => (
        <Row key={`${d.closed}-${d.name}-${i}`}>
          <RowTop>
            <RowName>{d.owner || 'Owner not recorded'}</RowName>
            <RowAmt>{money(d.amount)}</RowAmt>
          </RowTop>
          <RowSub>
            {day(d.closed)}
            {d.name ? ` · ${d.name}` : ''}
            {d.paymentType ? ` · ${d.paymentType}` : ''}
          </RowSub>
        </Row>
      ))}
    </Rows>
  );
};

/* [CLIENT-PULSE 2026-09-09, user] The whole "Client Pulse Details" tab.
   Written to the same brief as the rest of this page — plain English, every number explained in
   words — because a PM opening this tab has not read ClientPulse's scoring documentation and
   "Engagement velocity 50" means nothing on its own.

   ⛔ EVERY OUTCOME IS A SENTENCE, NOT AN ERROR. ClientPulse is a third party, so "we could not reach
   them" is a normal thing for this tab to say and must read as a calm, temporary state — never as
   the page being broken. [CP-POLISH 2026-09-10, user] Written for managers and senior executives:
   no talk of tunnels or keys-in-environments, and a Try again button wherever retrying can help. */
type PulsePanelProps = {
  pulse: CampaignPulse | null;
  loading: boolean;
  failed: boolean;
  website: string;
  /** This page's own current PM and monthly amount, to notice when ClientPulse disagrees. */
  ourPm: string | null;
  ourAmount: number | null;
  onRetry: () => void;
};
/* [CP-INSTANT 2026-09-10] The scores card only. The timeline is its own component (PulseTimeline)
   so it can load in the background and live through the loading states below, whose early returns
   would otherwise unmount it. */
const PulsePanel: React.FC<PulsePanelProps> = ({
  pulse, loading, failed, website, ourPm, ourAmount, onRetry,
}) => {
  // `!pulse && !failed` counts as loading too: between the history landing and the fetch effect
  // starting there is one render with neither flag set, which used to flash the failure card.
  if (loading || (!pulse && !failed)) {
    // [CP-INSTANT 2026-09-10] The scores card only. The timeline below no longer waits for the scores
    // (it shows as soon as its own page is ready), so it needs no placeholder here.
    return (
      <Card>
        <CardTitle>Client Pulse Details</CardTitle>
        <CardBody><SkeletonBox $h="150px" $radius="12px" /></CardBody>
      </Card>
    );
  }

  // A failure talking to OUR server, as opposed to ours failing to reach ClientPulse.
  if (failed || !pulse) {
    return (
      <Card>
        <CardTitle>Client Pulse Details</CardTitle>
        <NotBuilt>
          <p><strong>Client Pulse could not be loaded.</strong></p>
          <p>Something went wrong while fetching it. Please try again.</p>
          <RetryBtn type="button" onClick={onRetry}>Try again</RetryBtn>
        </NotBuilt>
      </Card>
    );
  }

  if (pulse.status !== 'ok') {
    // Four different reasons, four different sentences — a single "unavailable" would leave the
    // reader unable to tell "this client isn't on ClientPulse" (nothing to fix) from "our key was
    // rejected" (someone must act). Only a temporary failure offers Try again.
    const said: Record<Exclude<CampaignPulse['status'], 'ok'>, { head: string; body: string; retry: boolean }> = {
      no_account: {
        head: 'This website is not on ClientPulse.',
        body: 'ClientPulse has no account for ' + website + ', so there is nothing to score. Almost every live campaign is on ClientPulse, so it is worth asking them to add it.',
        retry: false,
      },
      not_configured: {
        head: 'Client Pulse is not connected on this server.',
        body: 'The ClientPulse access key is missing from this server\'s settings. It works as soon as the key is added.',
        retry: false,
      },
      key_rejected: {
        head: 'ClientPulse did not accept our access key.',
        body: 'The key may have expired or been replaced. ClientPulse can issue a new one, and old and new keys can run side by side while it is swapped.',
        retry: false,
      },
      unreachable: {
        head: 'Client Pulse is temporarily unavailable.',
        body: 'ClientPulse could not be reached just now. This is usually brief.',
        retry: true,
      },
    };
    const m = said[pulse.status] || said.unreachable;
    return (
      <Card>
        <CardTitle>Client Pulse Details</CardTitle>
        <NotBuilt>
          <p><strong>{m.head}</strong></p>
          <p>{m.body}</p>
          {m.retry && <RetryBtn type="button" onClick={onRetry}>Try again</RetryBtn>}
        </NotBuilt>
      </Card>
    );
  }

  const s = pulse.scores;
  const healthTone = zoneTone(s.health.zone);
  const riskTone = zoneTone(s.risk.zone);
  const kpis = [
    { name: 'Contact', v: s.kpis.communicationPulse, says: 'How much we are talking to them.', tip: PULSE_TIPS.contact },
    { name: 'Their mood', v: s.kpis.clientMood, says: 'How positive their replies read.', tip: PULSE_TIPS.mood },
    { name: 'Our replies', v: s.kpis.responseTime, says: 'How quickly we answer them.', tip: PULSE_TIPS.replies },
    { name: 'Meetings', v: s.kpis.meetingHealth, says: 'How their meetings are going.', tip: PULSE_TIPS.meetings },
  ];

  /* [CP-POLISH 2026-09-10, user] The footer is a source line, not a repeat of the left column: the
     PM and the monthly amount already sit in "About this campaign". ClientPulse's own PM / amount
     appear only when they DISAGREE with ours, which is the one case worth a reader's attention.
     Built as one string for the reason noted on the old version: JSX turns a line break before
     punctuation into a stray space ("9 Sep 2026 . These"). */
  const when = scoredAt(s.asOf.computedAt, s.asOf.snapshotDate);
  const differs: string[] = [];
  if (s.pm && ourPm && s.pm.trim().toLowerCase() !== ourPm.trim().toLowerCase()) differs.push(s.pm + ' as the PM');
  if (s.monthlyAmount != null && ourAmount != null && Math.abs(s.monthlyAmount - ourAmount) >= 0.01) {
    differs.push(money2(s.monthlyAmount) + ' a month');
  }

  return (
    <>
      <Card>
        <CardTitleRow>
          <span>Client Pulse Details</span>
          {/* ⛔ THIS BADGE READS THE `active` FIELD, NOT AN HTTP CODE. ClientPulse serves an
              archived account as a normal 200 with full scores; their own documentation still
              says a 404 means "not an active account", and trusting that would have shown 12 of
              our live campaigns as ordinary ones. Measured 2026-09-09. */}
          {!s.active && <Badge $tone="wait">Archived on ClientPulse</Badge>}
        </CardTitleRow>

        <PulseHeads>
          {/* [2026-09-09, user] The whole panel is the hover target, not just the number — a
              6px-tall bar or a two-word label would be a fussy thing to have to hit. */}
          <PulseHead $tone={healthTone} title={PULSE_TIPS.health}>
            <PulseHeadTop>
              <PulseLabel>How the campaign is doing</PulseLabel>
              <PulseZone $tone={healthTone}>{zoneLabel(s.health.zone)}</PulseZone>
            </PulseHeadTop>
            <PulseScore $tone={healthTone}><Score100 v={s.health.score} /></PulseScore>
            <PulseBar aria-hidden="true">
              <PulseBarFill $tone={healthTone} $pct={s.health.score ?? 0} />
            </PulseBar>
            <PulseSays>Higher is better.</PulseSays>
          </PulseHead>

          <PulseHead $tone={riskTone} title={PULSE_TIPS.risk}>
            <PulseHeadTop>
              <PulseLabel>Chance of losing them</PulseLabel>
              <PulseZone $tone={riskTone}>{zoneLabel(s.risk.zone)}</PulseZone>
            </PulseHeadTop>
            <PulseScore $tone={riskTone}><Score100 v={s.risk.score} /></PulseScore>
            <PulseBar aria-hidden="true">
              <PulseBarFill $tone={riskTone} $pct={s.risk.score ?? 0} />
            </PulseBar>
            {/* The one score on this page where a BIG number is BAD — said outright, because every
                other figure on this screen rewards being high. */}
            <PulseSays>Lower is better.</PulseSays>
          </PulseHead>
        </PulseHeads>

        <PulseKpis>
          {kpis.map((k) => (
            <div key={k.name} title={k.tip}>
              <KpiName>{k.name}</KpiName>
              <KpiValue $na={k.v == null}><Score100 v={k.v} /></KpiValue>
              <KpiSays>{k.v == null ? 'Not enough has happened yet to score this.' : k.says}</KpiSays>
            </div>
          ))}
        </PulseKpis>

        <Foot>
          {'Source: ClientPulse' + (when ? ' · last scored ' + when : '')}
          {differs.length > 0 && (
            <PulseNote>{'ClientPulse lists ' + differs.join(' and ') + ', which differs from this page.'}</PulseNote>
          )}
        </Foot>
      </Card>

    </>
  );
};

/* [CP-INSTANT 2026-09-10, user] ClientPulse's conversation timeline — kept apart from PulsePanel so
   it can load in the background and live through tab switches.
   ⭐ A THEME TOGGLE NEVER RELOADS IT. The page our server serves holds both colour sets, and a
   two-line script inside it (the only script its CSP allows) switches between them when told. The
   first theme rides the address's #fragment, frozen when the frame is created — changing the
   fragment later would add a browser-history entry on every toggle — and each later change is a
   postMessage. The reader's scroll position and opened messages stay exactly as they were. */
const PulseTimeline: React.FC<{
  website: string;
  /** The scores have loaded — the timeline sits under them, so it waits for them before showing. */
  show: boolean;
  embed: CampaignPulseEmbed | null;
  embedFailed: boolean;
  /** Bumped by Try again — a new key makes a new frame. */
  attempt: number;
  onRetry: () => void;
}> = ({ website, show, embed, embedFailed, attempt, onRetry }) => {
  const theme = useTheme();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const firstPreset = useRef(theme.colorScheme === 'dark' ? 'dark' : 'light');
  const src = timelineFrameSrc(website) + '#' + firstPreset.current;
  const [ready, setReady] = useState(false);
  useEffect(() => { setReady(false); }, [src, attempt]);
  // Tell the page which colours to show, on every toggle — also while the tab is hidden.
  useEffect(() => {
    frameRef.current?.contentWindow?.postMessage({ type: 'dt-theme', preset: theme.colorScheme }, '*');
  }, [theme.colorScheme]);

  const failed = embedFailed || (!!embed && (embed.status === 'unreachable' || embed.status === 'key_rejected'));
  // no_account / not_configured: the scores card has already said so, and twice would read as two faults.
  const absent = !!embed && (embed.status === 'no_account' || embed.status === 'not_configured');

  return (
    <>
      {show && failed && (
        <Card>
          <NotBuilt>
            <p><strong>The conversation timeline could not be loaded.</strong></p>
            <p>It could not be fetched just now. The scores above are not affected.</p>
            <RetryBtn type="button" onClick={onRetry}>Try again</RetryBtn>
          </NotBuilt>
        </Card>
      )}
      <PulseTimelineCard $hidden={!show || failed || absent}>
        <PulseFrameArea>
          {/* [CP-FRAME / CP-INSTANT 2026-09-10] The page comes from OUR server — their timeline, our
              colours for both themes, the app's scrollbars. `sandbox="allow-scripts"` WITHOUT
              allow-same-origin: our theme switch may run, but the page stays an opaque origin that
              can never read this app's cookies or call its API, and the server's CSP lets only that
              one hashed script run and nothing be fetched. No `loading="lazy"`: the frame loads
              while the tab is still hidden, so it is ready when the tab is opened.
              [2026-09-09, user] "remove this tooltip, no use of this" — deliberately NO `title`
              attribute (a native tooltip floated over the timeline). ⛔ Do not add it back:
              `aria-label` names it for a screen reader without drawing a tooltip. */}
          <PulseFrame
            key={attempt}
            ref={frameRef}
            src={src}
            sandbox="allow-scripts"
            aria-label={'Communication timeline for ' + website}
            referrerPolicy="no-referrer"
            onLoad={(e) => {
              e.currentTarget.contentWindow?.postMessage({ type: 'dt-theme', preset: theme.colorScheme }, '*');
              setReady(true);
            }}
          />
          {!ready && (
            <PulseFrameCover aria-hidden="true">
              <SkeletonBox $h="100%" $radius="11px" />
            </PulseFrameCover>
          )}
        </PulseFrameArea>
        <Foot>Newest first. Click any message to read it in full.</Foot>
      </PulseTimelineCard>
    </>
  );
};

/* ── Order Confirmation ──────────────────────────────────────────────────────
   [ORDER-CONFIRMATION 2026-09-21, user] "for each and every website we can see the order
   confirmation using this API … a month filter on the right side … by default set to current month
   or the latest month on which we get the payment … using the month filter he can also see that on
   that particular month what payment he got on the website."
   One month filter; under it one order-confirmation card per payment we received that month (our
   payments table, on the same month the Campaign Activities feed uses, so the two tabs agree), each
   holding the other team's record for that payment (their lookup API, called by our server —
   services/orderConfirmation) — see [OC-CARD] below.
   ⚠ THE API'S FIELDS ARE UNKNOWN — it answered [] for every website tried on 2026-09-21. The user
   chose to show EVERY field it sends, so a record is a grid of label/value pairs the server has
   already flattened and typed (date / money / link / text). Tidy the layout once real records land. */
/* The select is taller than a heading's text, so this title row trims its padding to stay the same
   height as every other card heading on the page. */
const OrderTitleRow = styled(CardTitleRow)`padding-top: 5px; padding-bottom: 5px;`;
const MonthPick = styled.label`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase;
  color: ${(p) => p.theme.muted};
`;
const OcHead = styled.h3`
  margin: 0; padding: 14px 16px 8px;
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] */
  color: ${(p) => p.theme.muted};
  border-top: 1px solid ${(p) => p.theme.line};
  &:first-child { border-top: none; }
`;
const OcRecordBox = styled.div`
  margin: 0 16px 14px;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 12px;
  overflow: hidden;
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;
const OcRecordTop = styled.div`
  padding: 9px 14px;
  font-size: 13px; font-weight: 700; color: ${(p) => p.theme.text};
  background: ${(p) => p.theme.bg};
  border-bottom: 1px solid ${(p) => p.theme.line};
  font-variant-numeric: tabular-nums;
`;
const OcFields = styled.dl`
  margin: 0; padding: 12px 14px;
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 11px 18px;
  dt {
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: ${(p) => p.theme.muted};
    @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] */
  }
  dd {
    margin: 2px 0 0; font-size: 13.5px; font-weight: 500; color: ${(p) => p.theme.text};
    overflow-wrap: anywhere; font-variant-numeric: tabular-nums;
  }
  a { color: ${(p) => p.theme.greenInk}; font-weight: 600; }
`;

/* [OC-CARD 2026-09-21, user] "I want a proper format of the order confirmation so that it should look
   good on the dashboard … a proper detail … not just a single line." The user chose ONE CARD PER
   PAYMENT: our payment row plus what this page already knows about the campaign (plan, PM, sales
   owner, phone) laid out like an order confirmation, with the Order Confirmation system's own record
   in a section inside the same card. The API sends data only, never a layout, and on 2026-09-21 it
   had nothing for any website — so the card is built to read fully on our data alone.
   Colours: theme tokens and the page's toneInk/toneTint only; no new token. */
/* The month at a glance, above the cards — same 1px-gap tile grid as the Lifetime card.
   [OC-ONLY 2026-09-21] Two tiles now: the "In OC system x of y" tile counted payments with no OC. */
const OcStats = styled(Totals)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-bottom: 1px solid ${(p) => p.theme.line};
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const OcNotice = styled.div`
  margin: 14px 16px 0; padding: 11px 14px;
  border-radius: 10px;
  background: ${(p) => toneTint(p, 'warn')};
  border: 1px solid ${(p) => toneInk(p, 'warn')}33;
  font-size: 13px; line-height: 1.45; color: ${(p) => p.theme.text};
  strong { display: block; color: ${(p) => toneInk(p, 'warn')}; }
  button { margin-top: 10px; }
`;
const OcCard = styled.article`
  margin: 14px 16px 0;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 12px;
  overflow: hidden;
  &:last-of-type { margin-bottom: 16px; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;
const OcCardHead = styled.header`
  display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between;
  gap: 10px 18px;
  padding: 14px 16px;
  background: ${(p) => p.theme.bg};
  border-bottom: 1px solid ${(p) => p.theme.line};
`;
const OcKicker = styled.div`
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-26] */
  color: ${(p) => p.theme.muted};
`;
const OcTitle = styled.div`
  margin-top: 3px;
  font-size: 16px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.3;
  color: ${(p) => p.theme.text}; overflow-wrap: anywhere;
`;
const OcSub = styled.div`
  margin-top: 2px; font-size: 12.5px; font-weight: 500; color: ${(p) => p.theme.muted};
`;
const OcMoney = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 7px;
  margin-left: auto;
`;
const OcAmount = styled.div<{ $tone: 'good' | 'bad' | 'warn' | 'flat' }>`
  font-size: 22px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.1;
  font-variant-numeric: tabular-nums; white-space: nowrap;
  color: ${(p) => (p.$tone === 'flat' ? p.theme.text : toneInk(p, p.$tone))};
`;
const OcBadges = styled.div`display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px;`;
/* [OC-TEMPLATE 2026-09-21, user] "I want to see it in the proper OC template fetched from the API."
   The user chose TEMPLATE ONLY: under the card head, the organisation's own finished OC document
   (the record's pdf_url), shown in the browser's PDF viewer with its toolbar hidden, scrolling
   inside the card. The two field grids that sat here are gone; the document already holds all of
   it. Open full size opens the full viewer in a new tab, which has print and download.
   [OC-PAGES 2026-09-21, user] The browser's PDF viewer is gone from the card: its grey frame and wide
   scrollbar could not be restyled ("why am I seeing this white thing", then "in the light mode the
   border becomes black"). The user chose to draw the pages ourselves: each page is an image
   (services/ocPdfPages, pdf.js) in our own scroll box, on the dashboard background, with the app's
   thin themed scrollbar. */
const OcDoc = styled.div`
  padding: 14px 16px 16px;
  & + & { border-top: 1px solid ${(p) => p.theme.line}; }
`;
const OcPages = styled.div`
  height: clamp(480px, 72vh / var(--app-zoom, 1), 1000px); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto; overflow-x: hidden;
  display: flex; flex-direction: column; gap: 12px;
  padding: 12px;
  border: 1px solid ${(p) => p.theme.line}; border-radius: 10px;
  background: ${(p) => p.theme.bg};
  > img {
    display: block; width: 100%; height: auto; flex-shrink: 0;
    border-radius: 4px; box-shadow: ${(p) => p.theme.shadow};
    /* [OC-DARK 2026-09-21, user] "make the oc as per our theme change" - picked the dark-mode filter
       (on trial; the user may ask to revert). The PDF is a fixed white document, so in the dark theme
       each drawn page is flipped: invert turns the white page dark grey (0.88, near the dark surface)
       and the grey text light; hue-rotate 180deg turns the inverted greens back to green. Colours are
       approximate. Only the pages are flipped now, never a frame. The light theme is untouched. */
    ${(p) => (p.theme.colorScheme === 'dark' ? 'filter: invert(0.88) hue-rotate(180deg);' : '')}
  }
  > p { margin: auto; padding: 0 24px; text-align: center; font-size: 13px; color: ${(p) => p.theme.muted}; }
`;
const OcDocBar = styled.div`display: flex; justify-content: flex-end; margin-top: 10px;`;
/* [OC-SPEED 2026-09-21] Wraps the always-mounted Order Confirmation panel; hidden exactly like the
   Client Pulse pane (PANE_HIDDEN), so a hidden OC document keeps loading. */
const OrderPane = styled.div<{ $hidden: boolean }>`
  min-width: 0;
  ${(p) => (p.$hidden ? PANE_HIDDEN : '')}
`;
// [BUTTON-STYLE 2026-09-30] A text link-button ("Open full size"): the secondary style.
const OcOpen = styled.a.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  &:focus-visible { outline: 2px solid ${(p) => p.theme.greenInk}; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

/** An order-confirmation date as their API wrote it. A bare date reads "18 Sep 2026"; a timestamp
 *  that names its zone is shown in IST like the app's clocks; one that names no zone is shown as
 *  written, because converting it would mean guessing their server's zone. */
const ocDate = (v: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return day(v);
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(v)) return scoredAt(v, null) || v;
  const hm = v.slice(11, 16);
  return day(v.slice(0, 10)) + (hm ? `, ${hm}` : '');
};
const OcValue: React.FC<{ f: CampaignOrderField }> = ({ f }) => {
  if (f.kind === 'date') return <>{ocDate(f.value)}</>;
  if (f.kind === 'money') return <>{money2(Number(f.value))}</>;
  // Only ever an http(s) address — the server types nothing else as a link.
  if (f.kind === 'link') return <a href={f.value} target="_blank" rel="noopener noreferrer">{f.value}</a>;
  return <>{f.value}</>;
};
/** [OC-PAGES] The OC's pages, drawn by pdf.js as images, one under the other. Page 1 shows as soon
 *  as it is drawn. The pane is always mounted ([OC-SPEED]), so this normally runs in the background
 *  before the tab is opened. */
const OcPdfPages: React.FC<{ website: string; url: string }> = ({ website, url }) => {
  const box = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    const made: string[] = [];
    setPages([]);
    setFailed(false);
    preloadPdfJs();
    // Drawn at the box's own width (known even while the pane is hidden) times the screen's density,
    // so the text stays sharp; capped so a very large screen does not draw huge pages.
    const cssWidth = Math.max(600, (box.current?.clientWidth || 1000) - 24);
    const widthPx = Math.min(2400, Math.round(cssWidth * (window.devicePixelRatio || 1)));
    fetchCampaignOrderPdf(website, url)
      .then((bytes) => drawPdfPages(bytes, widthPx, (u) => { made.push(u); setPages((p) => [...p, u]); }, () => live))
      .catch(() => { if (live) setFailed(true); });
    return () => {
      live = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [website, url]);

  return (
    // aria-label, never title: GlobalTooltip turns every title into a hover tooltip, and the user
    // asked for that one to go ("remove this tooltip").
    <OcPages ref={box} role="document" aria-label="Order confirmation document" aria-busy={!pages.length && !failed}>
      {pages.length > 0 ? (
        pages.map((u, i) => <img key={u} src={u} alt={`Order confirmation, page ${i + 1}`} />)
      ) : failed ? (
        <p>This order confirmation could not be shown here. Open full size below opens it in a new tab.</p>
      ) : (
        <SkeletonBox $h="100%" $radius="4px" />
      )}
    </OcPages>
  );
};

/** [OC-TEMPLATE] One order confirmation as the organisation issued it: its own PDF. A record with no
 *  PDF falls back to the field grid, so a confirmed order is never hidden. */
const OcDocument: React.FC<{ r: CampaignOrderRecord; website: string }> = ({ r, website }) => {
  if (!r.pdfUrl) {
    return (
      <OcFields>
        {r.fields.map((f, i) => (
          <div key={`${f.label}-${i}`}><dt>{f.label}</dt><dd><OcValue f={f} /></dd></div>
        ))}
      </OcFields>
    );
  }
  return (
    <OcDoc>
      <OcPdfPages website={website} url={r.pdfUrl} />
      <OcDocBar>
        <OcOpen href={r.pdfUrl} target="_blank" rel="noopener noreferrer">
          Open full size <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
        </OcOpen>
      </OcDocBar>
    </OcDoc>
  );
};
const OcRecordCard: React.FC<{ r: CampaignOrderRecord; n: number; website: string }> = ({ r, n, website }) => (
  <OcRecordBox>
    <OcRecordTop>{r.date ? `Order confirmation · ${ocDate(r.date)}` : `Order confirmation ${n}`}</OcRecordTop>
    <OcDocument r={r} website={website} />
  </OcRecordBox>
);

type OrderPanelProps = {
  order: CampaignOrder | null;
  failed: boolean;
  month: string | null;
  onMonth: (m: string) => void;
  onRetry: () => void;
  website: string;
};

/** One payment as an order confirmation. [OC-ONLY] The server sends only payments the Order
 *  Confirmation system has a record for, so every card here is a confirmed one.
 *  [OC-TEMPLATE] The head says which payment it is; the body is the OC document itself. */
const OcPaymentCard: React.FC<{ p: CampaignOrder['payments'][number]; website: string }> = ({ p, website }) => {
  const refundedAll = p.refunded > 0 && p.refunded >= p.amount - 0.005;
  const payState: { label: string; tone: 'good' | 'bad' | 'wait' | 'flat' } = refundedAll
    ? { label: 'Refunded', tone: 'bad' }
    : p.refunded > 0 ? { label: 'Partly refunded', tone: 'wait' }
    : p.disputed > 0 ? { label: 'Disputed', tone: 'wait' }
    : p.amount > 0 ? { label: 'Paid', tone: 'good' } : { label: 'No charge', tone: 'flat' };

  return (
    <OcCard>
      <OcCardHead>
        <div>
          <OcKicker>Order confirmation</OcKicker>
          <OcTitle>{p.invoice ? `Invoice ${p.invoice}` : 'Payment'}</OcTitle>
          <OcSub>{day(p.date)}{p.client ? ` · ${p.client}` : ''}</OcSub>
        </div>
        <OcMoney>
          <OcAmount $tone={refundedAll ? 'bad' : 'flat'}>{money2(p.amount)}</OcAmount>
          <OcBadges>
            <Badge $tone={payState.tone}>{payState.label}</Badge>
            <Badge $tone="good">Confirmed by OC system</Badge>
          </OcBadges>
        </OcMoney>
      </OcCardHead>

      {p.oc.map((r, i) => <OcDocument key={i} r={r} website={website} />)}
    </OcCard>
  );
};

const OrderPanel: React.FC<OrderPanelProps> = ({ order, failed, month, onMonth, onRetry, website }) => {
  if (!order && !failed) {
    return (
      <Card>
        <CardTitle>Order Confirmation</CardTitle>
        <CardBody><SkeletonBox $h="220px" $radius="12px" /></CardBody>
      </Card>
    );
  }
  // A failure talking to OUR server (the payments read), as opposed to the API being down.
  if (!order) {
    return (
      <Card>
        <CardTitle>Order Confirmation</CardTitle>
        <NotBuilt>
          <p><strong>Order Confirmation could not be loaded.</strong></p>
          <p>Something went wrong while fetching it. Please try again.</p>
          <RetryBtn type="button" onClick={onRetry}>Try again</RetryBtn>
        </NotBuilt>
      </Card>
    );
  }

  /* [OC-ONLY 2026-09-21, user] "If it is confirmed with the API that this is an OC, then I want to
     see. Otherwise, I don't want to see any data about that." The server now sends only confirmed
     payments and only months holding an order confirmation, so: API down → its note alone; no
     order confirmation at all → one plain line; otherwise the confirmed cards for the month. */
  const m = month && order.months.includes(month) ? month : order.defaultMonth;
  const pays = order.payments.filter((p) => p.month === m);
  const total = pays.reduce((t, p) => t + p.amount, 0);
  // Records the server could not tie to a payment: this month's, plus any with no date at all.
  const loose = order.unmatched.filter((r) => r.month === m || !r.month);
  const ocCount = pays.reduce((n, p) => n + p.oc.length, 0) + loose.length;
  const apiSaid: Record<Exclude<CampaignOrder['status'], 'ok'>, { head: string; body: string; retry: boolean }> = {
    not_configured: {
      head: 'Order Confirmation is not connected on this server.',
      body: 'The access key for the Order Confirmation system is missing from this server\'s settings. It works as soon as the key is added.',
      retry: false,
    },
    key_rejected: {
      head: 'The Order Confirmation system did not accept our access key.',
      body: 'The key may have expired or been replaced. The team that runs it can issue a new one.',
      retry: false,
    },
    unreachable: {
      head: 'Order confirmations are temporarily unavailable.',
      body: 'The Order Confirmation system could not be reached just now. Please try again in a moment.',
      retry: true,
    },
  };

  // The API could not be asked, so nothing can be confirmed — its note alone, no cards.
  if (order.status !== 'ok') {
    return (
      <Card>
        <CardTitle>Order Confirmation</CardTitle>
        <OcNotice role="status" style={{ marginBottom: 16 }}>
          <strong>{apiSaid[order.status].head}</strong>
          {apiSaid[order.status].body}
          {apiSaid[order.status].retry && <div><RetryBtn type="button" onClick={onRetry}>Try again</RetryBtn></div>}
        </OcNotice>
      </Card>
    );
  }
  if (!order.months.length) {
    return (
      <Card>
        <CardTitle>Order Confirmation</CardTitle>
        <NotBuilt>
          <p><strong>No order confirmation for this website.</strong></p>
          <p>The Order Confirmation system has no order confirmation on file for {website}. Once one is issued, it will appear here.</p>
        </NotBuilt>
      </Card>
    );
  }

  return (
    <Card>
      <OrderTitleRow>
        <span>Order Confirmation</span>
        <MonthPick>
          Month
          <GlassSelect liquid deferChange /* [FLUID-8 2026-10-12] instant pick */ value={m} onChange={(e) => onMonth(e.target.value)}>
            {order.months.map((x) => <option key={x} value={x}>{monthLong(x)}</option>)}
          </GlassSelect>
        </MonthPick>
      </OrderTitleRow>

      <OcStats>
        <div><span className="k">Order confirmations · {monthLong(m)}</span><span className="v">{ocCount}</span></div>
        <div><span className="k">Received</span><span className="v good">{money2(total)}</span></div>
      </OcStats>

      {pays.map((p, i) => <OcPaymentCard key={`${p.date}-${p.paymentId || i}`} p={p} website={website} />)}

      {loose.length > 0 && (
        <>
          <OcHead>
            <span>Order confirmations not tied to a payment</span>
            <span>{loose.length}</span>
          </OcHead>
          {loose.map((r, i) => <OcRecordCard key={`l-${i}`} r={r} n={i + 1} website={website} />)}
        </>
      )}
      {/* [OC-TEMPLATE 2026-09-21, user] No footer line under the cards ("remove this line"). */}
    </Card>
  );
};

/* ── page ───────────────────────────────────────────────────────────────── */
const CampaignHistoryPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const website = (params.get('website') || '').trim();

  const [midTab, setMidTab] = useState<MidTab>('activities');
  /* [PHONE-LAYOUT 2026-10-26, user] Phones: the three tabs are one sideways swipe row (Tabs), so the chosen tab is
     brought into view, centred, whenever it changes — a tab picked at the row's edge never stays half-hidden. */
  const isMobile = useIsMobile();
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = tabsRef.current;
    if (!isMobile || !row) return;
    const tab = row.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!tab) return;
    const off = tab.getBoundingClientRect().left - row.getBoundingClientRect().left;
    row.scrollTo({ left: row.scrollLeft + off - (row.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
  }, [isMobile, midTab]);
  const [data, setData] = useState<CampaignHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false); // [LOAD-STATES 2026-09-29] a failed load, not "no website"/404

  /* [CLIENT-PULSE 2026-09-09, user] Fetched in the background once the campaign itself has loaded,
     then kept. This call leaves our network for ClientPulse (~1-2 s), so it must never sit in the
     page's own load.
     ⚠ `pulseFailed` is separate from the page's own `error`: a ClientPulse outage must never
     blank the campaign history that loaded perfectly well beside it. */
  const [pulse, setPulse] = useState<CampaignPulse | null>(null);
  const [pulseLoading, setPulseLoading] = useState(false);
  const [pulseFailed, setPulseFailed] = useState(false);
  /* [CP-ROUTES 2026-09-10] Whether the timeline is available — its own fetch (server
     services/clientPulse), so a slow one never holds the scores back. [CP-FRAME 2026-09-10] The page
     itself is then loaded by the frame straight from our server, which always uses a fresh link, so
     nothing here can go stale. */
  const [embed, setEmbed] = useState<CampaignPulseEmbed | null>(null);
  const [embedFailed, setEmbedFailed] = useState(false);
  /* [CP-POLISH 2026-09-10] Bumped by the two Try again buttons. A fetch effect re-runs only when one
     of its inputs changes, and after a failure nothing else changes — so without these a retry
     would do nothing.
     [CP-REVIEW 2026-09-10] Two more things a retry must do, both found by the review workflow:
       • Drop the browser's cached answer first. Both routes answer HTTP 200 with the outcome in
         `status`, so services/api's GET cache kept an "unreachable" for 5 minutes and the retry was
         served that same failure without ever reaching the server. The '|' stops the scores key
         from also matching the timeline's.
       • A scores retry also retries a failed timeline. While the scores card shows its failure the
         timeline is hidden, so that card's button is the only one the reader can see. */
  const [pulseTry, setPulseTry] = useState(0);
  const [embedTry, setEmbedTry] = useState(0);
  const retryEmbed = () => {
    invalidateGetCacheByPrefix(['/campaign-history/pulse/embed|']);
    setEmbed(null); setEmbedFailed(false); setEmbedTry((n) => n + 1);
  };
  const retryPulse = () => {
    invalidateGetCacheByPrefix(['/campaign-history/pulse|']);
    setPulse(null); setPulseFailed(false); setPulseTry((n) => n + 1);
    if (embedFailed || (embed && embed.status !== 'ok')) retryEmbed();
  };

  const load = useCallback(async () => {
    if (!website) { setError('No website was given.'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setData(await fetchCampaignHistory(website));
      setRetryable(false); // [LOAD-STATES 2026-09-29]
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || 'Could not load this campaign.');
      setData(null);
      // [LOAD-STATES 2026-09-29] Not found / not allowed are final answers; anything else counts down and retries.
      setRetryable(!isForbidden(e) && Number(e?.response?.status) !== 404);
    } finally {
      setLoading(false);
    }
  }, [website]);

  useEffect(() => { load(); }, [load]);

  /* [CLIENT-PULSE 2026-09-09] Runs on the first open of the Pulse tab and whenever the website
     changes. The `pulse === null` guard is what makes it fire once rather than on every tab click;
     switching away and back re-renders from state without another round trip.

     ⛔⛔ `pulseLoading` MUST NOT BE A DEPENDENCY HERE, AND MUST NOT GUARD THIS EFFECT.
     It was both, and the tab hung on its skeleton for ever (seen 2026-09-09). The loop:
     setPulseLoading(true) changes a value this effect depends on -> React re-runs the effect ->
     it first runs the PREVIOUS run's cleanup, which sets `live = false` -> the request that is
     still in flight comes back and every callback is guarded out, so neither setPulse nor
     setPulseLoading(false) ever fires. A self-invalidating effect: it cancelled its own request.
     The rule that keeps this correct — never depend on state the effect itself sets. `pulse`
     alone decides whether to fetch, and it is only ever set once per website.

     ⚠ `live` still guards the callbacks, for the case it was written for: opening the tab and
     immediately navigating to another campaign. The website reset effect below nulls `pulse`,
     which re-runs this with the new domain. */
  /* [TAB-SPEED 2026-09-10, user] Starts as soon as the campaign itself has loaded — no longer on the
     first click of the tab — so the tab is normally ready by the time it is opened. Gated on the
     history so the page's own request goes first. The server keeps its 60 s per-domain cache. */
  const historyReady = !!data;
  useEffect(() => {
    if (!historyReady || !website || pulse !== null) return;
    let live = true;
    setPulseLoading(true);
    setPulseFailed(false);
    fetchCampaignPulse(website)
      // Both setters fire together in one callback rather than leaning on .finally(), so the
      // loading flag can never be left standing after a result has landed.
      .then((r) => { if (live) { setPulse(r); setPulseLoading(false); } })
      .catch(() => { if (live) { setPulseFailed(true); setPulseLoading(false); } });
    return () => { live = false; };
  }, [historyReady, website, pulse, pulseTry]);

  /* [CP-ROUTES 2026-09-10] The timeline link, fetched alongside the scores on the same rules — and
     the same ⛔: never depend on state this effect sets itself (`embedFailed`). */
  useEffect(() => {
    if (!historyReady || !website || embed !== null) return;
    let live = true;
    setEmbedFailed(false);
    // [CP-INSTANT 2026-09-10] No loading flag: the frame's own cover shows until its page arrives.
    fetchCampaignPulseEmbed(website)
      .then((r) => { if (live) setEmbed(r); })
      .catch(() => { if (live) setEmbedFailed(true); });
    return () => { live = false; };
  }, [historyReady, website, embed, embedTry]);
  // [CP-FRAME 2026-09-10] The 20-minute stale-link check that sat here is gone: the browser no longer
  // holds a link at all. The frame asks our server, which always uses one at most 60 s old.

  /* [CP-INSTANT 2026-09-10, user] "it should immediately reflect at the time of someone opens this
     page". Start building the timeline page on the server the moment this page opens — before the
     campaign's own history has even arrived, which is when the frame itself is created. A HEAD
     request runs the whole route (the server fetches, prepares and caches the page) but downloads
     nothing; when the frame asks a moment later it joins that work in flight or finds it cached.
     The frame's own address only adds a #theme, which never reaches the server, so both requests
     are the same page. A failure here is harmless — the frame simply does the work itself. */
  useEffect(() => {
    if (!website) return;
    fetch(timelineFrameSrc(website), { method: 'HEAD', credentials: 'include' }).catch(() => {});
  }, [website]);

  /* A different campaign is a different pulse — drop the old one so the tab refetches instead of
     showing the previous website's scores or timeline. */
  useEffect(() => {
    setPulse(null); setPulseFailed(false);
    setEmbed(null); setEmbedFailed(false);
  }, [website]);

  /* [ORDER-CONFIRMATION 2026-09-21, user] The Order Confirmation tab's data, fetched in the
     background once the campaign itself has loaded — the same rules as Client Pulse above: it leaves
     our network, so it never sits in the page's own load, and a failure never blanks the page.
     ⛔ Same trap as the pulse effect: never depend on state this effect sets itself (`orderFailed`).
     `orderMonth` is the reader's pick; null means "the month the server chose" (defaultMonth). */
  const [order, setOrder] = useState<CampaignOrder | null>(null);
  const [orderFailed, setOrderFailed] = useState(false);
  const [orderTry, setOrderTry] = useState(0);
  const [orderMonth, setOrderMonth] = useState<string | null>(null);
  /* [OC-SPEED 2026-09-21, user] "someone can click and within one or two seconds he can see the
     data". Starts the moment the page opens, ALONGSIDE the campaign's own request, no longer after
     it: the route needs nothing from the history, and their API alone takes ~1 s. */
  useEffect(() => {
    if (!website || order !== null) return;
    let live = true;
    setOrderFailed(false);
    fetchCampaignOrder(website)
      .then((r) => { if (live) setOrder(r); })
      .catch(() => { if (live) setOrderFailed(true); });
    return () => { live = false; };
  }, [website, order, orderTry]);
  // A different campaign is a different set of orders and months.
  useEffect(() => {
    setOrder(null); setOrderFailed(false); setOrderMonth(null);
  }, [website]);
  // Try again drops the browser's cached answer first — the route answers 200 for an API outage too.
  const retryOrder = () => {
    invalidateGetCacheByPrefix(['/campaign-history/order|']);
    setOrder(null); setOrderFailed(false); setOrderTry((n) => n + 1);
  };

  // [INV-HISTORY-BTN 2026-09-10, user] Opened from the Inventory tab's history button
  // (`?from=inventory`), the back arrow returns to Inventory; every other entry — My Inventory's
  // website link — returns to My Inventory. [BACK-ARROW] The arrow has no text, so backLabel is
  // now only its aria-label.
  const fromInventory = params.get('from') === 'inventory';
  const backLabel = fromInventory ? 'Inventory' : 'My Inventory';
  const goBack = () => navigate(fromInventory ? '/inventory' : '/my-inventory');

  if (loading) {
    return (
      <Page>
        <Shell>
          <Col><SkeletonBox $h="150px" $radius="14px" /><SkeletonBox $h="320px" $radius="14px" /></Col>
          <Col><SkeletonBox $h="560px" $radius="14px" /></Col>
          <Col><SkeletonBox $h="170px" $radius="14px" /><SkeletonBox $h="260px" $radius="14px" /></Col>
        </Shell>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page>
        {/* ⚠ The way back MUST be here. It normally lives in the Campaign History box, and on this
            branch that card never renders — without a button the page is a dead end. Same arrow as
            the box ([BACK-ARROW 2026-09-10, user]). */}
        <Msg>
          {/* [LOAD-STATES 2026-09-29, user] A failed load of a real website counts down and retries. */}
          {retryable ? <LoadFailed what="this campaign" onRetry={load} compact /> : (
          <p style={{ margin: 0, fontWeight: 600 }}>{error || 'Nothing to show.'}</p>
          )}
          {!retryable && (
          <p style={{ margin: '8px 0 0', fontSize: 13 }}>
            {fromInventory
              ? 'Go back to Inventory and use the history button next to a website to open its history.'
              : 'Go back to My Inventory and click a website there to open its history.'}
          </p>
          )}
          <MsgAction>
            <BackBtn type="button" onClick={goBack} aria-label={`Back to ${backLabel}`}>
              <i className="fas fa-arrow-left" />
            </BackBtn>
          </MsgAction>
        </Msg>
      </Page>
    );
  }

  const { months, activities, pmStints, sales, upsells, totals, identity, company, salesOwner, about } = data;
  const current = months.find((m) => !m.complete) || null;
  const live = (months[0]?.status || '').toLowerCase() !== 'cancelled';

  /* [TIMELINE 2026-09-10, user] Year → month groups for the timeline, and each month's inventory
     figures for its header line — the first row wins, the same way Current PM reads months[0]. */
  const timeline = groupTimeline(activities);
  const monthByYm = new Map<string, (typeof months)[number]>();
  for (const m of months) if (!monthByYm.has(m.month)) monthByYm.set(m.month, m);
  const renderMonthMeta = (mo: TimelineMonth) => {
    const upcoming = !!data.currentMonth && mo.ym > data.currentMonth;
    const running = mo.ym === data.currentMonth;
    // Only where the server wrote the month's PM summary — a month that collected nothing already
    // says so in its "No payment received" row, and "Retained $0 · 0%" would repeat it.
    const m = mo.summary ? monthByYm.get(mo.ym) : undefined;
    const bits: React.ReactNode[] = [];
    if (m) {
      bits.push(<span key="pm">{m.pm || 'PM not yet assigned'}</span>);
      if (upcoming) {
        bits.push(<span key="placed">{money2(m.placed)} placed</span>);
      } else {
        bits.push(<span key="ret">Retained {money2(m.received)} of {money2(m.placed)}</span>);
        // No % while the month is running — "0.0%" on a month still collecting reads as a failure.
        if (!running && m.placed > 0) {
          const rate = (m.received / m.placed) * 100;
          bits.push(rate >= 99.95
            ? <span key="pct">{pct(rate)}</span>
            : <MetaTone key="pct" $tone="warn">{pct(rate)}</MetaTone>);
        }
      }
      if (m.upsell > 0) bits.push(<span key="ups">Upsell {money2(m.upsell)}</span>);
      if (m.status) {
        const t = statusTone(m.status);
        bits.push(t === 'good'
          ? <MetaOk key="st"><i className="fas fa-check" />{m.status}</MetaOk>
          : <MetaTone key="st" $tone={t}>{m.status}</MetaTone>);
      }
    }
    if (!upcoming && !running && bits.length === 0) return null;
    return (
      <MonthMeta>
        {upcoming && <StateChip>Upcoming</StateChip>}
        {running && <StateChip $live>In progress</StateChip>}
        {bits.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && <MetaSep aria-hidden>·</MetaSep>}
            {b}
          </React.Fragment>
        ))}
      </MonthMeta>
    );
  };

  return (
    <Page>
      <Shell>
        {/* ── who this campaign is ── */}
        {/* Height comes from the right column. The left rail is a fixed ~492px of facts and the
            right column's minimum is ~744px, so in practice this never actually scrolls — the
            scroller is here for safety, not because the data is expected to overflow. */}
        <Col>
          <Card>
            {/* [2026-09-04, user] "the first website name should get replaced by the Campaign
                History thing". The card was printing the domain twice on many campaigns because
                ClientPulse/HubSpot often store company_name AS the domain — anesthesiaexperts.com
                showed it on both lines. So the company line now renders only when it actually says
                something different from the website. */}
            <IdHead>
              <IdTop>
                <BackBtn type="button" onClick={goBack} aria-label={`Back to ${backLabel}`}>
                  <i className="fas fa-arrow-left" />
                </BackBtn>
                <IdText>
                  <SiteName>Campaign History</SiteName>
                  <SubName>{data.website}</SubName>
                </IdText>
              </IdTop>
              {/* [2026-09-04, user] The COMPANY NAME line that used to sit here is gone — the card
                  carries the page name, the website and the status chips, nothing else. (It used to
                  render only when it differed from the website; that conditional is gone with it.) */}
              <ChipRow>
                <Badge $tone={live ? 'good' : 'bad'}>{live ? 'With us now' : 'Not with us'}</Badge>
                {months[0]?.type && <Badge $tone="flat">{months[0].type}</Badge>}
              </ChipRow>
            </IdHead>
          </Card>

          <Card>
            <CardTitle>About this campaign</CardTitle>
            <CardBody>
              {/* [CAMPAIGN-HISTORY 2026-09-04, user] EXACTLY these seven, in exactly this order —
                  the user's list, ending "thats it". Brand, Where, Lead source, Industry and
                  Company size were here and were CUT on that instruction; don't reinstate them
                  without being asked. The order reads top-down as the campaign's own story:
                  who owns it on the sales side → when it joined us → how to reach them → where it
                  stands now. */}
              <Facts>
                {/* [2026-09-04, user] Sales Owner comes from the SERVER's fall-through
                    (deal → payment → new sale → lead), not from Leads_Raw alone — reading only the
                    lead left this blank on ~59% of campaigns even when the owner was visible on
                    the Payments tab. */}
                {/* [SALES OWNER 2026-09-07, user] The LATEST sale's owner, then "(original was X)"
                    when the first sale was someone else. Always rendered: 228 of the 1,866 websites
                    in inventory have no sales record in any system (w2systems.com predates all of
                    it) and the user ruled those are left blank rather than guessed at. */}
                <div className="hero"><dt>Sales Owner</dt>
                  <dd>
                    {salesOwner ? salesOwner.name : '—'}
                    {salesOwner?.original && (
                      <span className="sub">(original was {salesOwner.original.name})</span>
                    )}
                  </dd></div>
                {/* [ABOUT 2026-09-07, user] "below sales we should see pm names and then the other
                    data" — the campaign's two owners sit together at the top of the card. */}
                <div className="hero"><dt>Current PM</dt><dd>{months[0]?.pm || '—'}</dd></div>
                {/* [CURRENT-AM 2026-09-10, user] "below that it should show the current AM" — the
                    latest month's AM Names cell (the Inventory column), read exactly the way Current
                    PM reads months[0]. Always rendered; "—" when that cell is blank. */}
                <div className="hero"><dt>Current AM</dt><dd>{months[0]?.am || '—'}</dd></div>
                {/* [2026-09-04, user] "this will just have the month when we got the first payment
                    from this" — so this is the first PAYMENT month, not the first inventory month
                    (they differ for 294 of 300 live campaigns) and not the first retention month
                    (245 of 300 were paid before any retention). Falls back to the first inventory
                    month only when a campaign has no payment at all. */}
                <div><dt>First Payment Month</dt>
                  <dd>{ym(totals.firstPaymentMonth || totals.firstMonth)}</dd></div>
                {/* [ABOUT 2026-09-04, user] A DIFFERENT fact from the line above: the present
                    unbroken run, not the first payment ever. artdogs.net first paid Jun 2025 but its
                    current run began Nov 2025. Shown only while the campaign is live — the user
                    removed the "Not with us since" line for cancelled ones, so an out campaign
                    simply has no run row (the status chip up top already says it is out). */}
                {about.currentRun && !about.currentRun.out && (
                  <div><dt>Continuously with us since</dt>
                    <dd>{ym(about.currentRun.since)} · {plural(about.currentRun.months, 'month')}</dd></div>
                )}
                {company?.phone_number && <div><dt>Contact Number</dt><dd>{company.phone_number}</dd></div>}
                {identity.emails.length > 0 && (
                  <div><dt>{plural(identity.emails.length, 'Email')}</dt>
                    <dd>{identity.emails.map((e) => <div key={e}>{e}</div>)}</dd></div>
                )}
                <div><dt>Latest status</dt><dd>{months[0]?.status || '—'}{current ? ' — that month has not finished yet' : ''}</dd></div>
                {/* [ABOUT 2026-09-04, user] The evidence behind the status: when money last came in
                    and how much. From the latest non-excluded, non-deleted payment. */}
                {about.lastPayment && (
                  <div><dt>Last payment Received</dt>
                    <dd>{day(about.lastPayment.date)} · {money2(about.lastPayment.amount)}</dd></div>
                )}
                <div><dt>Monthly amount</dt><dd>{money2(months[0]?.placed)}</dd></div>
                {/* [ABOUT 2026-09-04, user] Which entity invoices them and on what day. The entity
                    is the LATEST payment's account_name, not inventory.brand — brand agrees on
                    294 of 300 but holds stale values like "Bank Account" on the rest. */}
                {(about.billedVia || about.billingDay != null) && (
                  <div><dt>Billing</dt>
                    <dd>
                      {[
                        about.billedVia ? `via ${about.billedVia}` : null,
                        about.billingDay != null ? `bills on the ${ordinal(about.billingDay)}` : null,
                      ].filter(Boolean).join(' · ')}
                    </dd></div>
                )}
              </Facts>
            </CardBody>
          </Card>
        </Col>

        {/* ── the middle column: three sections ── */}
        {/* [2026-09-04, user] Campaign Activities / Client Pulse Details / Order Confirmation.
            Only the first is specified so far — the other two say so plainly instead of showing an
            empty card. The tab strip sits above the scroll pane so it never scrolls away. */}
        <PinnedCol>
          {/* [FLUID-8 2026-10-12, user] InstantPick: a tab lights up on the mouse PRESS; the section switch (a whole-page
              render — Campaign Activities mounts its full timeline) follows as a transition. Quiet, like every switch. */}
          <InstantPick value={midTab} onPick={setMidTab}>
            {(shownTab, press) => (
              <Tabs ref={tabsRef /* [PHONE-LAYOUT 2026-10-26] phones: the chosen tab into view */} role="tablist" aria-label="Campaign sections">
                {MID_TABS.map((t) => (
                  <Tab
                    key={t.key}
                    role="tab"
                    type="button"
                    aria-selected={shownTab === t.key}
                    $active={shownTab === t.key}
                    {...press(t.key)}
                    /* [2026-09-04, user] No title attribute — it only produced a native browser tooltip
                       repeating the label already printed on the tab. */
                  >
                    {t.label}
                  </Tab>
                ))}
              </Tabs>
            )}
          </InstantPick>

          <PinnedScroll role="tabpanel">
          {/* [OC-SPEED 2026-09-21, user] Order Confirmation is ALWAYS mounted too and only HIDDEN on the
              other tabs (same PANE_HIDDEN as Client Pulse), so each OC document starts loading in
              the background and is usually on screen the moment the tab is clicked.
              ⚠ It sits FIRST, ahead of the Client Pulse pane, on purpose: PinnedScroll grows its
              :last-child, and the Client Pulse pane needs that on its own tab. */}
          <OrderPane $hidden={midTab !== 'order'}>
            <OrderPanel
              order={order}
              failed={orderFailed}
              month={orderMonth}
              onMonth={setOrderMonth}
              onRetry={retryOrder}
              // [OC-TEMPLATE 2026-09-21] The card's campaign-facts grid is gone; only the website is needed.
              website={data.website}
            />
          </OrderPane>
          {/* [CP-INSTANT 2026-09-10, user] Client Pulse is ALWAYS mounted once the campaign has
              loaded and only HIDDEN on the other tabs — so its timeline loads in the background, is
              ready the moment the tab is opened, and a tab switch never reloads it.
              ⚠ It sits FIRST on purpose: PinnedScroll grows its :last-child, and a hidden pane at
              the end would take that from the visible card. */}
          <PulsePane $hidden={midTab !== 'pulse'}>
            <PulsePanel
              pulse={pulse}
              loading={pulseLoading}
              failed={pulseFailed}
              website={data.website}
              // [CP-POLISH 2026-09-10] The same figures "About this campaign" prints, so a
              // disagreement note compares like with like. A $0 month has no amount to compare.
              ourPm={months[0]?.pm || null}
              ourAmount={months[0] && months[0].placed > 0 ? months[0].placed : null}
              onRetry={retryPulse}
            />
            <PulseTimeline
              website={data.website}
              // [CP-INSTANT 2026-09-10] Shown while the scores are still loading too — it no longer
              // waits for them (a cold scores call once held a ready timeline back 2 s). Hidden only
              // when the scores END in a failure, which the card above explains.
              show={!pulseFailed && (!pulse || pulse.status === 'ok')}
              embed={embed}
              embedFailed={embedFailed}
              attempt={embedTry}
              onRetry={retryEmbed}
            />
          </PulsePane>
          {/* [TIMELINE 2026-09-10, user] See the "Campaign timeline" styles for the why. */}
          {midTab === 'activities' && (
          <TimelineCard>
            <CardTitleRow>
              <span>Campaign timeline</span>
              {activities.length > 0 && (
                <TitleRange>
                  {ym(activities[activities.length - 1].month)} – {ym(activities[0].month)}
                </TitleRange>
              )}
            </CardTitleRow>
            <Timeline>
              {timeline.map((yr) => (
                <React.Fragment key={yr.year}>
                  <YearDivider>{yr.year}</YearDivider>
                  {yr.months.map((mo) => (
                    <MonthSection key={mo.ym}>
                      <MonthHead>
                        <span>{monthLong(mo.ym)}</span>
                        {renderMonthMeta(mo)}
                      </MonthHead>
                      {mo.rows.length > 0 && (
                        <EventList>
                          {mo.rows.map((a, i) => {
                            const kicker = kickerFor(a);
                            return (
                              <Event key={`${a.kind}-${a.date}-${i}`} $major={a.major} $tone={a.tone}>
                                <When $day={!!a.dated} dateTime={a.dated ? a.date : a.month}>
                                  {a.dated ? day(a.date) : ym(a.month)}
                                </When>
                                <Marker $major={a.major} $tone={a.tone} />
                                <EvBody>
                                  <EvTitle $major={a.major} $tone={a.tone}>
                                    {kicker && <Kicker $tone={a.tone}>{kicker}</Kicker>}
                                    {a.title}
                                  </EvTitle>
                                  {a.detail && <EvDetail>{a.detail}</EvDetail>}
                                </EvBody>
                                <EvAmount $major={a.major} $tone={a.tone}>
                                  {/* [NEW-SALE-NO-AMOUNT 2026-09-10, user] "Sold by …" only ANNOUNCES the new
                                      sale — no amount. The money is shown once, on the payment line it
                                      arrived on ("First payment from this client"); printing the deal
                                      amount here too made a reader count the same $200 twice. */}
                                  {a.kind !== 'sold' && a.amount != null && a.amount > 0 ? signedAmount(a) : null}
                                </EvAmount>
                              </Event>
                            );
                          })}
                        </EventList>
                      )}
                    </MonthSection>
                  ))}
                </React.Fragment>
              ))}
            </Timeline>
          </TimelineCard>
          )}

          {/* [CLIENT-PULSE 2026-09-09, user] ClientPulse's read on this website — their two headline
              scores, four KPIs and their own conversation timeline — now lives in the always-mounted
              PulsePane at the top of this scroller ([CP-INSTANT 2026-09-10]). */}

          {/* [ORDER-CONFIRMATION 2026-09-21, user] See the "Order Confirmation" styles for the why.
              [OC-SPEED] It now lives in the always-mounted OrderPane at the top of this scroller. */}
          </PinnedScroll>
        </PinnedCol>

        {/* ── summary cards ── */}
        <Col>
          <Card>
            {/* [BACK-ARROW 2026-09-10, user] The "← My Inventory" button that sat on the right of
                this heading is GONE — the way back is now the arrow in the Campaign History box. */}
            <CardTitle>Lifetime</CardTitle>
            {/* [MONEY 2026-09-04, user] "Received" is now ALL the money over finished months —
                retention, new sales, upsells, PIA, recovery and anything never classified — so it
                is the number the feed's "Payment received" events add up to; the two can no longer
                disagree. The old "Received" tile was retention only and is relabelled "Retained",
                and the gross tile now says "Total Payment Received" in full (user, 2026-09-07).
                Placed reads before Retained because the money is placed first and kept second.
                which is what Kept % has always divided (retained ÷ placed). Kept stays a retention
                rate on purpose: gross can exceed placed once upsells land, and a "Kept 140%" would
                be nonsense. */}
            {/* [LIFETIME-BOX 2026-09-10, user] "placed and retained will work for the inventory and
                upsell will work for that but below that we will have one box as 'Total Payment
                Received' and it will have the complete payment that we got from the client". So the
                four inventory/upsell boxes read first, and the full-width total sits UNDER them —
                every payment from the client, whether or not a month has an inventory row (e.g. a
                first payment made before the campaign's first inventory month). It moved from the
                top rather than being added twice. The figure is unchanged: money can only be on file
                once it has arrived, so it is already "everything received up to now". */}
            <Totals>
              <div><span className="k">Placed</span><span className="v">{money(totals.placed)}</span></div>
              <div><span className="k">Retained</span><span className="v">{money(totals.received)}</span></div>
              <div><span className="k">Kept</span><span className="v">{pct(totals.keptPct)}</span></div>
              <div><span className="k">Upsell won</span><span className="v good">{money(totals.upsell)}</span></div>
              <div className="wide"><span className="k">Total Payment Received</span><span className="v good">{money(totals.gross)}</span></div>
            </Totals>
            {/* [LIFETIME 2026-09-10, user] Every figure above is the website's overall record now —
                the running month and months with no inventory row included. Only rows created
                AHEAD of time (a month that has not started) stay out of Placed / Retained / Kept. */}
            {totals.monthsExcluded.length > 0 && (
              <Foot>
                {listMonths(totals.monthsExcluded)} {totals.monthsExcluded.length === 1 ? 'has' : 'have'} not
                started yet, so {totals.monthsExcluded.length === 1 ? 'it is' : 'they are'} not counted in
                Placed, Retained or Kept.
              </Foot>
            )}
          </Card>

          <Card>
            <CardTitle>
              {sales.length ? `Sold ${plural(sales.length, 'time')}` : 'Sales'}
            </CardTitle>
            <DealRows deals={sales} emptyText="No sale is recorded in HubSpot for this website." />
            {sales.length > 1 && (
              <Foot>
                A campaign can be won more than once, so every sale is shown rather than guessing a
                single salesperson.
              </Foot>
            )}
          </Card>

          <Card>
            <CardTitle>Project managers</CardTitle>
            <Rows>
              {pmStints.map((s) => (
                <Row key={`${s.pm}-${s.from}`}>
                  <RowTop>
                    <RowName>{s.pm}</RowName>
                    <RowAmt>{money(s.received)}</RowAmt>
                  </RowTop>
                  <RowSub>
                    {s.from === s.to ? ym(s.from) : `${ym(s.from)} – ${ym(s.to)}`} ·{' '}
                    {plural(s.months, 'month')}
                    {s.keptPct != null ? ` · kept ${pct(s.keptPct)}` : ''}
                    {s.endedCancelled ? ' · cancelled under them' : ''}
                  </RowSub>
                </Row>
              ))}
            </Rows>
          </Card>

          <Card>
            <CardTitle>Upsells won · {upsells.length}</CardTitle>
            <DealRows deals={upsells} emptyText="No upsell has been won on this campaign." />
          </Card>

          <Card>
            <CardTitle>How we found this history</CardTitle>
            <CardBody>
              <Facts>
                <div><dt>Matched on website</dt>
                  <dd>{identity.websites.join(', ')} — {plural(totals.monthsWithUs, 'month')}</dd></div>
                <div><dt>First payment</dt>
                  <dd>{ym(totals.firstPaymentMonth || totals.firstMonth)}</dd></div>
              </Facts>
            </CardBody>
            {/* [2026-09-04, user] "use the website part only". Everything on this page is matched
                on the website and nothing else, so the card says exactly that rather than listing
                a second matching route that no longer exists. */}
            <Foot>
              Everything here is matched on this website alone. Email is not used to pull in
              history, so nothing from another client can appear.
            </Foot>
          </Card>
        </Col>
      </Shell>
    </Page>
  );
};

export default CampaignHistoryPage;
